import * as PIXI from "pixi.js";
import { audioPaths } from "../constants.js";
import { button, panel, text } from "../components.js";
import { phoneCombatControlsLayout } from "../layout.js";
import { clamp, currentLanguage } from "../utils.js";

export const combatMethods = {
  combatPanel(width, height) {
    const container = new PIXI.Container();
    if (!this.state.combat) {
      container.addChild(text("No active combat.", { fontSize: 16 }));
      return container;
    }
    const combat = this.state.combat;
    if (this.isPendingCombatParticipant(combat)) {
      const pending = this.pendingCombatPlayerNames(combat).join(", ") || "You";
      container.addChild(text(`Joining next round: ${pending}`, { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" }));
      const rows = [
        `Round ${combat.round || 1} | ${combat.phase || "planning"}`,
        ...(combat.players || []).map((fighter) => `Fighting: ${fighter.name} HP ${fighter.hp}/${fighter.max_hp}`),
        ...(combat.enemies || []).map((fighter) => `Enemy: ${fighter.name} HP ${fighter.hp}/${fighter.max_hp}`),
      ];
      const waiting = this.listPanel(rows, width, Math.max(120, height - 34));
      waiting.position.set(0, 34);
      container.addChild(waiting);
      return container;
    }
    const header = text(`Round ${combat.round || 1} | ${combat.phase || "planning"} | ${combat.status}`, { fontSize: 16, fill: 0xf2e6c7, fontWeight: "900" });
    container.addChild(header);
    const columnsY = 34;
    const columnWidth = Math.floor((width - 14) / 2);
    const players = this.listPanel((combat.players || []).map((fighter) => `Ally: ${fighter.name} HP ${fighter.hp}/${fighter.max_hp}`), columnWidth, 130);
    const enemies = this.listPanel((combat.enemies || []).map((fighter) => `Enemy: ${fighter.name} HP ${fighter.hp}/${fighter.max_hp}`), columnWidth, 130);
    players.position.set(0, columnsY);
    enemies.position.set(columnWidth + 14, columnsY);
    container.addChild(players, enemies);

    const hand = combat.hands?.[this.state.player?.id] || [];
    const handTitle = text("Hand", { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900" });
    handTitle.position.set(0, 176);
    container.addChild(handTitle);
    const handLines = hand.length
      ? hand.map((card) => `${card.name || card.card_id} (${card.action_type || "action"})`)
      : ["No cards in hand."];
    const handList = this.listPanel(handLines, columnWidth, Math.max(80, height - 228));
    handList.position.set(0, 202);
    container.addChild(handList);

    const intentTitle = text("Enemy Intent", { fontSize: 14, fill: 0xf2e6c7, fontWeight: "900" });
    intentTitle.position.set(columnWidth + 14, 176);
    container.addChild(intentTitle);
    const intents = (combat.enemy_intents || []).map((intent) => `${intent.actor_name}: ${intent.target_name || "Ready"}`);
    const logLines = (combat.log || []).slice(-4).map((line) => `Log: ${line}`);
    const rightLines = [...(intents.length ? intents : ["No intent revealed."]), ...logLines];
    const rightList = this.listPanel(rightLines, columnWidth, Math.max(80, height - 228));
    rightList.position.set(columnWidth + 14, 202);
    container.addChild(rightList);
    return container;
  },

  drawCombatSurface() {
    const combat = this.displayCombatForReplay(this.state.combat);
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    this.layers.modal.addChild(new PIXI.Graphics().rect(0, 0, width, height).fill(0x050606));
    if (!combat) {
      const empty = text("No active combat.", { fontSize: 22, fill: 0xf2e6c7, fontWeight: "900" });
      empty.anchor.set(0.5);
      empty.position.set(width / 2, height / 2);
      this.layers.modal.addChild(empty);
      return;
    }
    if (combat.status !== "active" && !this.state.combatReplay) {
      this.drawCombatSummary(combat, width, height);
      return;
    }
    if (!this.state.combatReplay && this.isPendingCombatParticipant(combat)) {
      this.drawCombatWaitingScreen(combat, width, height);
      return;
    }
    const secondsLeft = Math.max(0, Math.ceil((combat.round_ends_at || 0) - Date.now() / 1000));
    const replaying = Boolean(this.state.combatReplay);
    const locked = replaying || secondsLeft <= 0;
    const players = this.combatPlayers(combat);
    const livingPlayers = players.filter((fighter) => fighter.hp > 0);
    const readyCount = (combat.ready_actor_ids || []).filter((actorId) => livingPlayers.some((fighter) => fighter.id === actorId)).length;
    const selfReady = (combat.ready_actor_ids || []).includes(this.state.player?.id);
    this.ensureCombatSelection();
    this.drawCombatArena(combat, players, locked);
    if (replaying) {
      this.drawCombatResolveQueueBar(combat, players);
    }
    if (!replaying) {
      this.drawCombatPlanningTimer(combat, secondsLeft, locked, readyCount, livingPlayers.length);
      this.drawCombatControls(combat, players, locked, selfReady, readyCount, livingPlayers.length, { resolving: secondsLeft <= 0 });
      if (!locked) {
        this.drawHoveredPlannedCardArrow(players, combat.enemies || []);
      }
    }
    if (!replaying) {
      this.drawCombatPilePopup();
    }
    this.drawCombatPileShuffleAnimations();
  },

  isPendingCombatParticipant(combat = this.state.combat) {
    const playerId = this.state.player?.id;
    return Boolean(playerId && combat?.pending_participant_ids?.includes(playerId));
  },

  pendingCombatPlayerNames(combat = this.state.combat) {
    const pendingPlayers = combat?.pending_players || [];
    if (pendingPlayers.length) {
      return pendingPlayers.map((fighter) => fighter.name);
    }
    const pendingIds = new Set(combat?.pending_participant_ids || []);
    return (this.state.world?.players || [])
      .filter((player) => pendingIds.has(player.id))
      .map((player) => player.name);
  },

  drawCombatWaitingScreen(combat, width, height) {
    const bgTexture = this.assetTextures.get(combat.arena_image || "combat_arena") || this.assetTextures.get("combat_arena");
    if (bgTexture) {
      const bg = new PIXI.Sprite(bgTexture);
      const scale = Math.max(width / bgTexture.width, height / bgTexture.height);
      bg.width = bgTexture.width * scale;
      bg.height = bgTexture.height * scale;
      bg.position.set((width - bg.width) / 2, (height - bg.height) / 2);
      this.layers.modal.addChild(bg);
    }
    this.layers.modal.addChild(new PIXI.Graphics().rect(0, 0, width, height).fill({ color: 0x000000, alpha: 0.58 }));
    const frameWidth = Math.min(540, width - 32);
    const frameHeight = Math.min(360, height - 48);
    const frame = panel(frameWidth, frameHeight, { fill: 0x090a0a, alpha: 0.9, stroke: 0xd7aa5f, strokeAlpha: 0.78, radius: 6 });
    frame.position.set((width - frameWidth) / 2, (height - frameHeight) / 2);
    this.layers.modal.addChild(frame);

    const title = text("Joining next round", { fontSize: 24, fill: 0xf3d28a, fontWeight: "900" });
    title.anchor.set(0.5, 0);
    title.position.set(width / 2, frame.y + 28);
    const subtitle = text(`Round ${combat.round || 1} is already in progress. You will enter when the next planning round starts.`, {
      fontSize: 13,
      fill: 0xcabf9e,
      fontWeight: "700",
      wordWrap: true,
      wordWrapWidth: frameWidth - 56,
      align: "center",
    });
    subtitle.anchor.set(0.5, 0);
    subtitle.position.set(width / 2, frame.y + 66);
    this.layers.modal.addChild(title, subtitle);

    const activeNames = (combat.players || []).map((fighter) => `${fighter.name} ${fighter.hp}/${fighter.max_hp} HP`);
    const pendingNames = this.pendingCombatPlayerNames(combat);
    const enemyNames = (combat.enemies || []).map((fighter) => `${fighter.name} ${fighter.hp}/${fighter.max_hp} HP`);
    const rows = [
      ...(activeNames.length ? activeNames.map((line) => `Fighting: ${line}`) : ["Fighting: no active players"]),
      ...(pendingNames.length ? pendingNames.map((line) => `Waiting: ${line}`) : []),
      ...(enemyNames.length ? enemyNames.map((line) => `Enemy: ${line}`) : []),
    ];
    const list = this.listPanel(rows, frameWidth - 56, Math.max(120, frameHeight - 170));
    list.position.set(frame.x + 28, frame.y + 118);
    const close = button("Close", 92, 32, () => { this.state.activeWindow = null; this.renderUi(); }, { fill: 0x171817, alpha: 0.9, fontSize: 12 });
    close.position.set(width / 2 - 46, frame.y + frameHeight - 48);
    this.layers.modal.addChild(list, close);
  },

  drawCombatHeader(combat, players, readyCount, partySize, secondsLeft, locked) {
    const width = this.state.viewport.width;
    const headerWidth = Math.min(560, width - 28);
    const header = panel(headerWidth, 58, { fill: 0x050606, alpha: 0.72, stroke: 0xc7a052, strokeAlpha: 0.32, radius: 4 });
    header.position.set(14, 14);
    this.layers.modal.addChild(header);
    const selfView = players.find((fighter) => fighter.id === this.state.player?.id) || this.state.player;
    const title = text(`${selfView?.name || "Combat"} HP ${selfView?.hp ?? 0}/${selfView?.max_hp ?? selfView?.max_hp ?? 1}`, { fontSize: 18, fill: 0xf1e6c8, fontWeight: "900" });
    title.position.set(28, 23);
    const round = text(`Round ${combat.round || 1} - ${locked ? "Resolving" : `Planning: ${secondsLeft}s`} - ${readyCount}/${partySize || 1} ready`, { fontSize: 13, fill: 0xa99b78, fontWeight: "700" });
    round.position.set(28, 48);
    const timer = panel(92, 36, { fill: 0x090a0a, alpha: 0.74, stroke: 0xe6b859, strokeAlpha: 0.78 });
    timer.position.set(width - 112, 16);
    const timerText = text(locked ? "Locked" : `${secondsLeft}s`, { fontSize: 16, fill: 0xf3d28a, fontWeight: "900" });
    timerText.anchor.set(0.5);
    timerText.position.set(width - 66, 26);
    const close = button("Close", 76, 30, () => { this.state.activeWindow = null; this.renderUi(); }, { fill: 0x171817, alpha: 0.84, fontSize: 12 });
    close.position.set(width - 198, 19);
    this.layers.modal.addChild(title, round, timer, timerText, close);
  },

  drawCombatPlanningTimer(combat, secondsLeft, locked, readyCount, partySize) {
    const width = this.state.viewport.width;
    const safe = this.isPhoneLandscape() ? this.safeRect() : { x: 0, y: 0, right: width };
    const timer = panel(150, 58, { fill: 0x090a0a, alpha: 0.92, stroke: 0xffcf64, strokeAlpha: 0.95, radius: 6 });
    timer.position.set(safe.right - 158, safe.y + 8);
    const timerText = text(locked ? "Locked" : `${secondsLeft}s`, { fontSize: 24, fill: 0xffdf7a, fontWeight: "900", stroke: { color: 0x050505, width: 3 } });
    timerText.anchor.set(0.5, 0);
    timerText.position.set(safe.right - 83, safe.y + 12);
    const ready = text(`${readyCount}/${partySize || 1} ready`, { fontSize: 11, fill: 0xf0d59a, fontWeight: "900" });
    ready.anchor.set(0.5, 0);
    ready.position.set(safe.right - 83, safe.y + 43);
    this.layers.modal.addChild(timer, timerText, ready);
  },

  drawCombatArena(combat, players, locked) {
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    const arena = new PIXI.Container();
    arena.position.set(0, 0);
    this.layers.modal.addChild(arena);
    const bgTexture = this.assetTextures.get(combat.arena_image || "combat_arena") || this.assetTextures.get("combat_arena");
    const bg = bgTexture ? new PIXI.Sprite(bgTexture) : null;
    if (bg) {
      const scale = Math.max(width / bgTexture.width, height / bgTexture.height);
      bg.width = bgTexture.width * scale;
      bg.height = bgTexture.height * scale;
      bg.position.set((width - bg.width) / 2, (height - bg.height) / 2);
      arena.addChild(bg);
    } else {
      arena.addChild(new PIXI.Graphics().rect(0, 0, width, height).fill(0x161411));
    }
    arena.addChild(new PIXI.Graphics().rect(0, 0, width, height).fill({ color: 0x000000, alpha: 0.22 }));
    const replay = this.currentCombatReplayStep();
    const actorId = replay?.event?.actor_id || null;
    const targetId = replay?.event?.target_id || null;
    const hoveredPlannedEvent = locked ? null : this.hoveredPlannedCombatEvent();
    const queueEvent = hoveredPlannedEvent || this.hoveredCombatQueueEvent();
    const queueActorId = queueEvent?.actor_id || null;
    const queueTargetId = queueEvent?.target_id || null;
    const dragTarget = this.state.combatDrag?.active ? this.state.combatDrag.dropTarget : null;
    players.forEach((fighter, index) => {
      const slot = this.combatFormationPosition("player", index, Math.max(1, players.length), width, height);
      arena.addChild(this.combatantCard(fighter, "player", slot, {
        selected: fighter.id === this.state.selectedCombatAllyTargetId,
        plans: this.getPlannedActions(fighter.id),
        locked,
        acting: actorId === fighter.id ? replay : null,
        impact: targetId === fighter.id ? replay : null,
        queuedActor: queueActorId === fighter.id,
        queuedTarget: queueTargetId === fighter.id,
        dropTarget: dragTarget?.type === "combatant" && dragTarget.id === fighter.id,
      }));
    });
    const visibleEnemies = (combat.enemies || []).filter((fighter) => fighter.hp > 0);
    visibleEnemies.forEach((fighter, index) => {
      const slot = this.combatFormationPosition("enemy", index, Math.max(1, visibleEnemies.length), width, height);
      arena.addChild(this.combatantCard(fighter, "enemy", slot, {
        selected: fighter.id === this.state.selectedCombatTargetId,
        intentDetails: this.state.combatReplay ? null : this.combatEnemyIntent(fighter),
        plans: this.getPlannedActions(fighter.id),
        locked,
        acting: actorId === fighter.id ? replay : null,
        impact: targetId === fighter.id ? replay : null,
        queuedActor: queueActorId === fighter.id,
        queuedTarget: queueTargetId === fighter.id,
        dropTarget: dragTarget?.type === "combatant" && dragTarget.id === fighter.id,
      }));
    });
    this.drawCombatChatPopups(arena, combat, players, width, height);
    if (queueEvent && !hoveredPlannedEvent) {
      this.drawCombatQueueArrow(arena, queueEvent, players, visibleEnemies, width, height);
    }
    if (replay?.event) {
      this.drawCombatReplayEffects(arena, replay, players, visibleEnemies, width, height);
    }
    const centerLabel = replay?.event
      ? `${replay.event.actor_name} - ${this.combatActionLabel(replay.event)}`
      : locked ? "Resolving plans" : `Round ${combat.round || 1}`;
    const center = text(centerLabel, { fontSize: 18, fill: 0xf1c873, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
    center.anchor.set(0.5);
    center.position.set(width / 2, Math.max(112, height * 0.22));
    arena.addChild(center);
  },

  currentCombatReplayStep() {
    const replay = this.state.combatReplay;
    if (!replay?.events?.length) {
      return null;
    }
    const elapsed = performance.now() - replay.startedAt;
    let elapsedBefore = 0;
    let index = 0;
    for (; index < replay.events.length; index += 1) {
      const duration = this.combatEventDurationMs(replay.events[index]);
      if (elapsed <= elapsedBefore + duration + replay.gapMs || index === replay.events.length - 1) {
        break;
      }
      elapsedBefore += duration + replay.gapMs;
    }
    const eventDuration = this.combatEventDurationMs(replay.events[index]);
    const stepElapsed = elapsed - elapsedBefore;
    const progress = clamp(stepElapsed / eventDuration, 0, 1);
    return {
      event: replay.events[index],
      index,
      progress,
      count: replay.events.length,
      active: stepElapsed <= eventDuration,
      stepElapsed,
      duration: eventDuration,
      gapProgress: eventDuration > 0 && replay.gapMs > 0 ? clamp((stepElapsed - eventDuration) / replay.gapMs, 0, 1) : 1,
    };
  },

  displayCombatForReplay(combat) {
    const replay = this.state.combatReplay;
    if (!combat || !replay?.baseCombat) {
      return combat;
    }
    const step = this.currentCombatReplayStep();
    const visibleCombat = structuredClone(replay.baseCombat);
    const eventsToApply = replay.events.slice(0, step ? step.index : 0);
    if (step?.event && step.progress >= this.combatEventHpApplyProgress(step.event)) {
      eventsToApply.push(step.event);
    }
    eventsToApply.forEach((event) => this.applyReplayEventToCombat(visibleCombat, event));
    visibleCombat.log = combat.log || visibleCombat.log;
    visibleCombat.status = combat.status;
    visibleCombat.combat_events = combat.combat_events || [];
    return visibleCombat;
  },

  applyReplayEventToCombat(combat, event) {
    if (!event?.target_id) {
      return;
    }
    const target = (combat.players || []).find((fighter) => fighter.id === event.target_id)
      || (combat.enemies || []).find((fighter) => fighter.id === event.target_id);
    if (!target) {
      return;
    }
    if (event.result === "block" && event.healing) {
      const values = this.combatBlockEventValues(event);
      target.replay_block_values = target.replay_block_values || { melee: 0, ranged: 0, mental: 0 };
      ["melee", "ranged", "mental"].forEach((zone) => {
        target.replay_block_values[zone] = Number(target.replay_block_values[zone] || 0) + Number(values[zone] || 0);
      });
      target.replay_block_value = Number(target.replay_block_value || 0) + Number(event.healing || 0);
      return;
    }
    if (event.block_values_after && Object.keys(event.block_values_after).length) {
      target.replay_block_values = {
        melee: Math.max(0, Math.round(Number(event.block_values_after.melee || 0))),
        ranged: Math.max(0, Math.round(Number(event.block_values_after.ranged || 0))),
        mental: Math.max(0, Math.round(Number(event.block_values_after.mental || 0))),
      };
      target.replay_block_value = this.totalBlockValue(target.replay_block_values);
    }
    if (event.damage) {
      target.hp = Math.max(0, Number(target.hp || 0) - Number(event.damage || 0));
    }
    if (event.healing && event.result !== "block") {
      target.hp = Math.min(Number(target.max_hp || target.hp || 0), Number(target.hp || 0) + Number(event.healing || 0));
    }
    (event.statuses_applied || []).forEach((statusType) => {
      const sourceTarget = this.combatantInCombat(this.state.combat, event.target_id);
      const sourceStatus = (sourceTarget?.statuses || []).find((status) => status.type === statusType) || { type: statusType, rounds: 1, power: 1 };
      target.statuses = this.mergedCombatStatuses([...(target.statuses || []), sourceStatus]);
    });
  },

  combatEventHpApplyProgress(event) {
    return this.combatEventAnimation(event).numberAt;
  },

  combatEventDurationMs(event) {
    return this.combatEventAnimation(event).durationMs;
  },

  combatEventAnimation(event) {
    const animation = event?.animation || {};
    const defaultDamageAt = event?.result === "stunned" || event?.result === "escaped" ? 0.5 : 0.75;
    const damageAt = this.clampedAnimationProgress(animation.damage_at, defaultDamageAt);
    const impactAt = this.clampedAnimationProgress(animation.impact_at, damageAt);
    const numberAt = this.clampedAnimationProgress(animation.number_at, damageAt);
    const projectileStart = this.clampedAnimationProgress(animation.projectile_start, 0.12);
    const projectileEnd = this.clampedAnimationProgress(animation.projectile_end, impactAt);
    return {
      kind: String(animation.kind || (event?.action_type === "ranged" ? "projectile" : event?.action_type === "mental" ? "cast" : "melee")),
      key: String(animation.key || event?.action || ""),
      projectile: animation.projectile ? String(animation.projectile) : "",
      durationMs: Math.max(420, Math.min(4800, Number(animation.duration_ms || 920) * 1.6)),
      damageAt,
      impactAt,
      numberAt,
      projectileStart,
      projectileEnd: Math.max(projectileStart, projectileEnd),
    };
  },

  clampedAnimationProgress(value, fallback) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
      return clamp(fallback, 0, 1);
    }
    return clamp(number, 0, 1);
  },

  combatReplayWindowProgress(progress, start, duration) {
    return clamp((progress - start) / duration, 0, 1);
  },

  combatantReplayPosition(combatantId, players, enemies, width, arenaHeight) {
    const playerIndex = players.findIndex((fighter) => fighter.id === combatantId);
    if (playerIndex >= 0) {
      return this.combatFormationPosition("player", playerIndex, Math.max(1, players.length), width, arenaHeight);
    }
    const enemyIndex = enemies.findIndex((fighter) => fighter.id === combatantId);
    if (enemyIndex >= 0) {
      return this.combatFormationPosition("enemy", enemyIndex, Math.max(1, enemies.length), width, arenaHeight);
    }
    return null;
  },

  drawCombatReplayEffects(arena, replay, players, enemies, width, arenaHeight) {
    const event = replay.event;
    const animation = this.combatEventAnimation(event);
    const actor = this.combatantReplayPosition(event.actor_id, players, enemies, width, arenaHeight);
    const target = this.combatantReplayPosition(event.target_id, players, enemies, width, arenaHeight);
    if (!actor || !target) {
      return;
    }
    const actorPoint = { x: actor.x, y: actor.y - 58 * actor.scale };
    const targetPoint = { x: target.x, y: target.y - 58 * target.scale };
    const impactProgress = this.combatReplayWindowProgress(replay.progress, animation.impactAt, 0.34);
    if (event.result === "block") {
      this.drawCombatBlockPop(arena, event, targetPoint, target.scale, replay.progress, animation);
      return;
    }
    if (animation.kind === "projectile" || event.action_type === "ranged") {
      const flightDuration = Math.max(0.01, animation.projectileEnd - animation.projectileStart);
      const flight = this.combatReplayWindowProgress(replay.progress, animation.projectileStart, flightDuration);
      if (flight > 0 && flight < 1) {
        this.drawCombatProjectile(arena, event, actorPoint, targetPoint, flight);
      }
    }
    if (impactProgress > 0 && impactProgress < 1) {
      const color = event.healing ? 0x75e992 : event.action_type === "mental" ? 0x9a7cff : 0xff6a45;
      const radius = 22 + impactProgress * 58;
      arena.addChild(new PIXI.Graphics().circle(targetPoint.x, targetPoint.y, radius).stroke({ color, width: 5, alpha: 1 - impactProgress }));
    }
    const numberProgress = this.combatReplayWindowProgress(replay.progress, animation.numberAt, 0.36);
    const blockedHit = Number(event.blocked_damage || 0) > 0;
    if (numberProgress > 0 && numberProgress < 1 && (event.damage || event.healing || event.result || blockedHit)) {
      const entries = [];
      if (event.healing) {
        entries.push({ value: `+${event.healing}`, fill: 0x7df29a, x: 0, y: 0 });
      } else {
        if (event.damage) {
          entries.push({ value: `-${event.damage}`, fill: 0xff745b, x: blockedHit ? -24 : 0, y: 0 });
        }
        if (blockedHit) {
          entries.push({ value: `-${event.blocked_damage} block`, fill: 0xffdf7a, x: event.damage ? 28 : 0, y: event.damage ? -34 : 0 });
        }
        if (!event.damage && !blockedHit) {
          entries.push({ value: event.result, fill: 0xf3d37d, x: 0, y: 0 });
        }
      }
      entries.forEach((entry) => {
        const fontSize = entry.value.includes("block") ? 34 : 40;
        const number = text(entry.value, { fontSize, fill: entry.fill, fontWeight: "900", stroke: { color: 0x050505, width: 7 } });
        number.anchor.set(0.5);
        const alpha = Math.max(0.28, 1 - numberProgress * 0.72);
        const x = targetPoint.x + entry.x;
        const y = targetPoint.y + entry.y - 48 - numberProgress * 18;
        number.alpha = alpha;
        number.position.set(x, y);
        const backing = panel(number.width + 22, fontSize + 12, { fill: 0x050505, alpha: 0.42 * alpha, stroke: entry.fill, strokeAlpha: 0.32 * alpha, radius: 4 });
        backing.position.set(x - backing.width / 2, y - backing.height / 2);
        arena.addChild(backing, number);
      });
    }
  },

  combatBlockEventZone(event) {
    const zone = String(event?.animation?.block_zone || "");
    if (["melee", "ranged", "mental"].includes(zone)) {
      return zone;
    }
    return ["melee", "ranged", "mental"].includes(event?.action_type) ? event.action_type : "melee";
  },

  combatBlockEventValues(event) {
    const raw = event?.animation?.block_values || {};
    const values = {
      melee: Math.max(0, Math.round(Number(raw.melee || 0))),
      ranged: Math.max(0, Math.round(Number(raw.ranged || 0))),
      mental: Math.max(0, Math.round(Number(raw.mental || 0))),
    };
    if (this.totalBlockValue(values) > 0) {
      return values;
    }
    values[this.combatBlockEventZone(event)] = Math.max(0, Math.round(Number(event?.healing || 0)));
    return values;
  },

  drawCombatBlockPop(arena, event, targetPoint, targetScale, progress, animation) {
    const show = this.combatReplayWindowProgress(progress, Math.max(0, animation.numberAt - 0.12), 0.46);
    if (show <= 0 || show >= 1) {
      return;
    }
    const values = this.combatBlockEventValues(event);
    const pop = Math.sin(show * Math.PI);
    const alpha = Math.min(1, (1 - show) * 1.35);
    const group = new PIXI.Container();
    group.alpha = alpha;
    group.position.set(targetPoint.x, targetPoint.y - 36 * targetScale - pop * 18);
    const ring = new PIXI.Graphics()
      .circle(0, 10, (40 + pop * 28) * targetScale)
      .stroke({ color: 0xffe59a, width: 5, alpha: 0.82 });
    const shield = new PIXI.Graphics()
      .moveTo(0, -48 * targetScale)
      .lineTo(42 * targetScale, -24 * targetScale)
      .quadraticCurveTo(34 * targetScale, 34 * targetScale, 0, 55 * targetScale)
      .quadraticCurveTo(-34 * targetScale, 34 * targetScale, -42 * targetScale, -24 * targetScale)
      .closePath()
      .fill({ color: 0xe7c56f, alpha: 0.92 })
      .stroke({ color: 0x241508, width: 3 });
    group.addChild(ring, shield);
    this.drawCombatantBlockBadge(group, values, 0, 70 * targetScale, targetScale, { popScale: 1.25 + pop * 0.35 });
    arena.addChild(group);
  },

  drawCombatProjectile(arena, event, actorPoint, targetPoint, progress) {
    const x = actorPoint.x + (targetPoint.x - actorPoint.x) * progress;
    const y = actorPoint.y + (targetPoint.y - actorPoint.y) * progress - Math.sin(progress * Math.PI) * 28;
    const projectileKey = this.combatProjectileKey(event);
    const texture = this.assetTextures.get(projectileKey);
    if (texture) {
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.position.set(x, y);
      sprite.width = projectileKey === "projectile_rock" ? 36 : 58;
      sprite.height = projectileKey === "projectile_rock" ? 36 : 30;
      sprite.rotation = Math.atan2(targetPoint.y - actorPoint.y, targetPoint.x - actorPoint.x);
      arena.addChild(sprite);
      return;
    }
    const color = event.action_type === "mental" ? 0x9a7cff : 0xf4c35f;
    arena.addChild(new PIXI.Graphics().circle(x, y, 10).fill({ color, alpha: 0.96 }));
  },

  combatProjectileKey(event) {
    const projectile = String(event?.animation?.projectile || "").toLowerCase();
    if (projectile) {
      return `projectile_${projectile}`;
    }
    const action = String(event?.action || "").toLowerCase();
    if (action.includes("rock") || action.includes("throw") || action.includes("bomb") || action.includes("flask")) {
      return "projectile_rock";
    }
    return event?.action_type === "ranged" ? "projectile_rock" : "projectile_firebolt";
  },

  combatActionLabel(event) {
    const action = String(event?.action || "action").replace(/_/g, " ");
    if (event?.result === "stunned") return "stunned";
    if (event?.result === "escaped") return "escaped";
    if (event?.result === "dodged") return "dodged";
    if (event?.healing) return `${action} +${event.healing}`;
    if (event?.damage) return `${action} -${event.damage}`;
    return action;
  },

  hoveredCombatQueueEvent() {
    const hovered = Number(this.state.hoveredCombatQueueIndex);
    if (Number.isInteger(hovered) && hovered >= 0) {
      return this.state.combatReplay?.events?.[hovered] || null;
    }
    return null;
  },

  hoveredPlannedCombatEvent() {
    const cardInstanceId = this.state.hoveredCombatCardInstanceId;
    if (!cardInstanceId || !this.state.player) {
      return null;
    }
    const planned = this.localPlanForCard(cardInstanceId);
    if (!planned || planned.assignment !== "action" || !planned.target_id) {
      return null;
    }
    const card = this.combatCardByInstance(cardInstanceId);
    return {
      actor_id: this.state.player.id,
      target_id: planned.target_id,
      action: card?.card_id || cardInstanceId,
      action_name: card?.name || cardInstanceId,
      action_type: card?.action_type || "melee",
    };
  },

  combatResolveQueueEntries(replay = this.currentCombatReplayStep()) {
    const events = this.state.combatReplay?.events || [];
    if (!events.length || !replay) {
      return [];
    }
    const startIndex = replay.active ? replay.index : replay.index + 1;
    return events.slice(startIndex).map((event, offset) => ({
      event,
      index: startIndex + offset,
      active: replay.active && offset === 0,
    }));
  },

  combatActionIconKey(event) {
    const animationKey = String(event?.animation?.key || "");
    if (animationKey && this.assetTextures.has(`card_${animationKey}`)) return `card_${animationKey}`;
    if (animationKey && this.assetTextures.has(animationKey)) return animationKey;
    const actionKey = String(event?.action || "");
    if (actionKey && this.assetTextures.has(`card_${actionKey}`)) return `card_${actionKey}`;
    if (actionKey && this.assetTextures.has(actionKey)) return actionKey;
    return "card_default";
  },

  combatantById(id, players = this.combatPlayers(), enemies = this.state.combat?.enemies || []) {
    return [...players, ...enemies].find((fighter) => fighter.id === id) || null;
  },

  drawCombatResolveQueueBar(combat, players) {
    const width = this.state.viewport.width;
    const replay = this.currentCombatReplayStep();
    const entries = this.combatResolveQueueEntries(replay);
    if (!entries.length) {
      return;
    }
    const margin = 30;
    const barWidth = width - margin * 2;
    const tileWidth = clamp(Math.floor((barWidth - 28) / Math.min(10, entries.length)), 116, 154);
    const tileHeight = 58;
    const gap = 8;
    const barHeight = 74;
    const bar = panel(barWidth, barHeight, { fill: 0x050606, alpha: 0.78, stroke: 0x55d37a, strokeAlpha: 0.82, radius: 8 });
    bar.position.set(margin, 18);
    this.layers.modal.addChild(bar);
    const mask = new PIXI.Graphics().roundRect(margin + 10, 24, barWidth - 20, barHeight - 12, 6).fill(0xffffff);
    const row = new PIXI.Container();
    row.mask = mask;
    this.layers.modal.addChild(row, mask);
    const slideOffset = replay?.active ? 0 : (tileWidth + gap) * (1 - (replay?.gapProgress ?? 1));
    const visibleCount = Math.ceil((barWidth - 22) / (tileWidth + gap)) + 1;
    entries.slice(0, visibleCount).forEach((entry, offset) => {
      const tile = this.combatResolveQueueTile(entry, combat, players, tileWidth, tileHeight, entry.active);
      tile.position.set(margin + 16 + offset * (tileWidth + gap) + slideOffset, 26);
      row.addChild(tile);
    });
  },

  combatResolveQueueTile(entry, combat, players, width, height, active) {
    const { event, index } = entry;
    const hovered = this.state.hoveredCombatQueueIndex === index;
    const tile = new PIXI.Container();
    tile.eventMode = "static";
    tile.cursor = "pointer";
    tile.on("pointerover", (pointerEvent) => {
      pointerEvent.stopPropagation?.();
      this.state.hoveredCombatQueueIndex = index;
      this.renderUi();
    });
    tile.on("pointerout", (pointerEvent) => {
      pointerEvent.stopPropagation?.();
      if (this.state.hoveredCombatQueueIndex === index) {
        this.state.hoveredCombatQueueIndex = null;
        this.renderUi();
      }
    });
    const frame = panel(width, height, {
      fill: active ? 0x24351f : hovered ? 0x173829 : 0x101412,
      alpha: active || hovered ? 0.98 : 0.92,
      stroke: active ? 0xffdb72 : hovered ? 0x72ff9c : 0x355d3e,
      strokeAlpha: active || hovered ? 0.95 : 0.72,
      radius: 7,
    });
    tile.addChild(frame);
    const actor = this.combatantById(event.actor_id, players, combat.enemies || []);
    const actorTexture = this.combatTexture(actor?.visual_key, actor?.kind === "enemy" ? "enemy" : "player");
    const portrait = new PIXI.Sprite(actorTexture);
    portrait.anchor.set(0.5, 1);
    portrait.width = 32;
    portrait.height = 48;
    portrait.position.set(22, 51);
    tile.addChild(portrait);
    if (event.result === "block") {
      this.drawCombatQueueShieldIcon(tile, width - 30, 29, 24);
      const actionLabel = text(`Block +${event.healing || 0}`, {
        fontSize: 10,
        fill: 0xffdf7a,
        fontWeight: "900",
        wordWrap: true,
        wordWrapWidth: width - 62,
      });
      actionLabel.position.set(42, 9);
      const actorLabel = text(event.actor_name || actor?.name || "Actor", {
        fontSize: 9,
        fill: actor?.kind === "enemy" ? 0xffb394 : 0xaadfbc,
        fontWeight: "700",
        wordWrap: true,
        wordWrapWidth: width - 52,
      });
      actorLabel.position.set(42, 34);
      tile.addChild(actionLabel, actorLabel);
      return tile;
    }
    const iconTexture = this.assetTextures.get(this.combatActionIconKey(event));
    if (iconTexture) {
      const icon = new PIXI.Sprite(iconTexture);
      icon.anchor.set(0.5);
      icon.width = 30;
      icon.height = 30;
      icon.position.set(width - 21, 20);
      tile.addChild(icon);
    }
    const actionLabel = text(String(event.action_name || event.action || "Action").replace(/_/g, " "), {
      fontSize: 10,
      fill: 0xf3e6c4,
      fontWeight: "900",
      wordWrap: true,
      wordWrapWidth: width - 62,
    });
    actionLabel.position.set(42, 9);
    const actorLabel = text(event.actor_name || actor?.name || "Actor", {
      fontSize: 9,
      fill: 0xaadfbc,
      fontWeight: "700",
      wordWrap: true,
      wordWrapWidth: width - 52,
    });
    actorLabel.position.set(42, 34);
    tile.addChild(actionLabel, actorLabel);
    return tile;
  },

  drawCombatQueueShieldIcon(container, x, y, size) {
    const shadow = new PIXI.Graphics()
      .moveTo(x + 2, y - size * 0.78 + 3)
      .lineTo(x + size * 0.78 + 2, y - size * 0.26 + 3)
      .quadraticCurveTo(x + size * 0.62 + 2, y + size * 0.72 + 3, x + 2, y + size)
      .quadraticCurveTo(x - size * 0.62 + 2, y + size * 0.72 + 3, x - size * 0.78 + 2, y - size * 0.26 + 3)
      .closePath()
      .fill({ color: 0x000000, alpha: 0.42 });
    const shield = new PIXI.Graphics()
      .moveTo(x, y - size * 0.78)
      .lineTo(x + size * 0.78, y - size * 0.26)
      .quadraticCurveTo(x + size * 0.62, y + size * 0.72, x, y + size)
      .quadraticCurveTo(x - size * 0.62, y + size * 0.72, x - size * 0.78, y - size * 0.26)
      .closePath()
      .fill({ color: 0xd8b971, alpha: 0.98 })
      .stroke({ color: 0x20130a, width: 2.4 });
    const shine = new PIXI.Graphics()
      .moveTo(x - size * 0.2, y - size * 0.42)
      .lineTo(x + size * 0.28, y - size * 0.16)
      .lineTo(x + size * 0.08, y + size * 0.46)
      .stroke({ color: 0xfff1bd, width: 2, alpha: 0.76 });
    container.addChild(shadow, shield, shine);
  },

  drawCombatQueueArrow(arena, event, players, enemies, width, arenaHeight) {
    const actor = this.combatantReplayPosition(event.actor_id, players, enemies, width, arenaHeight);
    const target = this.combatantReplayPosition(event.target_id || event.actor_id, players, enemies, width, arenaHeight);
    if (!actor || !target) {
      return;
    }
    const selfTarget = !event.target_id || event.target_id === event.actor_id;
    const graphics = new PIXI.Graphics();
    if (selfTarget) {
      const x = actor.x;
      const top = actor.y - 185 * actor.scale;
      const bottom = actor.y - 122 * actor.scale;
      graphics.moveTo(x, top).lineTo(x, bottom).stroke({ color: 0xdfffea, width: 2, alpha: 0.88 });
      graphics.moveTo(x - 8, bottom - 10).lineTo(x, bottom).lineTo(x + 8, bottom - 10).stroke({ color: 0xdfffea, width: 2, alpha: 0.88 });
      arena.addChild(graphics);
      return;
    }
    const from = { x: actor.x, y: actor.y - 96 * actor.scale };
    const to = { x: target.x, y: target.y - 96 * target.scale };
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const end = { x: to.x - Math.cos(angle) * 42 * target.scale, y: to.y - Math.sin(angle) * 42 * target.scale };
    graphics.moveTo(from.x, from.y).lineTo(end.x, end.y).stroke({ color: 0xdfffea, width: 2, alpha: 0.82 });
    graphics
      .moveTo(end.x - Math.cos(angle - 0.55) * 14, end.y - Math.sin(angle - 0.55) * 14)
      .lineTo(end.x, end.y)
      .lineTo(end.x - Math.cos(angle + 0.55) * 14, end.y - Math.sin(angle + 0.55) * 14)
      .stroke({ color: 0xdfffea, width: 2, alpha: 0.82 });
    arena.addChild(graphics);
  },

  drawHoveredPlannedCardArrow(players, enemies) {
    const event = this.hoveredPlannedCombatEvent();
    if (!event) {
      return;
    }
    const hand = this.currentCombatHand();
    const index = hand.findIndex((card) => card.instance_id === this.state.hoveredCombatCardInstanceId);
    if (index < 0) {
      return;
    }
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    const { y } = this.combatControlsLayout();
    const handY = y + 128;
    const availableWidth = width - 240;
    const cardWidth = Math.max(104, Math.min(142, availableWidth / Math.max(1, hand.length) + 16));
    const gap = hand.length > 1 ? Math.min(8, (availableWidth - cardWidth * hand.length) / (hand.length - 1)) : 0;
    const startX = width / 2 - (cardWidth * hand.length + gap * (hand.length - 1)) / 2;
    const from = {
      x: startX + index * (cardWidth + gap) + cardWidth / 2,
      y: handY + 4,
    };
    const target = this.combatantReplayPosition(event.target_id, players, enemies, width, height);
    if (!target) {
      return;
    }
    const to = { x: target.x, y: target.y - 96 * target.scale };
    const control = {
      x: (from.x + to.x) / 2,
      y: Math.min(from.y, to.y) - 70,
    };
    const angle = Math.atan2(to.y - control.y, to.x - control.x);
    const end = {
      x: to.x - Math.cos(angle) * 42 * target.scale,
      y: to.y - Math.sin(angle) * 42 * target.scale,
    };
    const arrow = new PIXI.Graphics();
    arrow
      .moveTo(from.x, from.y)
      .quadraticCurveTo(control.x, control.y, end.x, end.y)
      .stroke({ color: 0xdfffea, width: 3, alpha: 0.9 });
    arrow
      .moveTo(end.x - Math.cos(angle - 0.55) * 16, end.y - Math.sin(angle - 0.55) * 16)
      .lineTo(end.x, end.y)
      .lineTo(end.x - Math.cos(angle + 0.55) * 16, end.y - Math.sin(angle + 0.55) * 16)
      .stroke({ color: 0xdfffea, width: 3, alpha: 0.9 });
    this.layers.tooltip.addChild(arrow);
  },

  drawCombatDragGhost() {
    const drag = this.state.combatDrag;
    if (!drag?.active) {
      return;
    }
    const card = this.combatCardByInstance(drag.cardInstanceId);
    if (!card) {
      return;
    }
    const width = Math.min(150, drag.width || 132);
    const height = Math.min(164, drag.height || 154);
    const x = drag.current.x - width / 2;
    const y = drag.current.y - height / 2;
    const valid = Boolean(drag.dropTarget);
    const ghost = this.combatHandCard(card, { x, y, width, height, disabled: true, preview: true }).view;
    ghost.alpha = valid ? 0.94 : 0.68;
    const detail = text(valid ? this.combatDropTargetLabel(drag.dropTarget) : "No target", {
      fontSize: 11,
      fill: valid ? 0x8dffc0 : 0xffa28d,
      fontWeight: "900",
      stroke: { color: 0x050505, width: 4 },
      wordWrap: true,
      wordWrapWidth: width + 24,
    });
    detail.anchor.set(0.5, 0);
    detail.position.set(drag.current.x, y + height + 8);
    this.layers.tooltip.addChild(ghost, detail);
  },

  combatDropTargetLabel(dropTarget) {
    if (!dropTarget) {
      return "No target";
    }
    if (dropTarget.type === "defense") {
      return `Block: ${dropTarget.defenseType}`;
    }
    return `Cast: ${this.combatantName(dropTarget.id)}`;
  },

  combatantCard(fighter, side, slot, options = {}) {
    const container = new PIXI.Container();
    container.position.set(slot.x, slot.y);
    if (options.acting) {
      const direction = side === "player" ? 1 : -1;
      const meleeOffset = options.acting.event?.action_type === "melee" ? Math.sin(options.acting.progress * Math.PI) * 30 * direction : 0;
      container.x += meleeOffset;
    }
    container.eventMode = options.locked || fighter.hp <= 0 ? "none" : "static";
    container.cursor = container.eventMode === "static" ? "pointer" : "default";
    container.on("pointertap", (event) => {
      event.stopPropagation?.();
      this.handleCombatantClick(fighter, side);
    });
    const selectedColor = side === "enemy" ? 0xe06b42 : 0x62a8e8;
    const glow = new PIXI.Graphics().ellipse(0, 108 * slot.scale, 74 * slot.scale, 18 * slot.scale).fill({ color: 0x000000, alpha: 0.54 });
    container.addChild(glow);
    if (options.selected || options.dropTarget) {
      container.addChild(new PIXI.Graphics().ellipse(0, 108 * slot.scale, 86 * slot.scale, 24 * slot.scale).stroke({ color: selectedColor, width: 3, alpha: 0.95 }));
    }
    if (options.dropTarget) {
      container.addChild(new PIXI.Graphics().ellipse(0, 70 * slot.scale, 102 * slot.scale, 126 * slot.scale).stroke({ color: 0xffdc73, width: 5, alpha: 0.9 }));
    }
    if (options.acting) {
      const pulse = 0.45 + Math.sin(options.acting.progress * Math.PI) * 0.45;
      container.addChild(new PIXI.Graphics().ellipse(0, 70 * slot.scale, 94 * slot.scale, 116 * slot.scale).stroke({ color: 0xf6c15f, width: 4, alpha: pulse }));
    }
    if (options.queuedActor || options.queuedTarget) {
      const color = options.queuedActor ? 0x78ff9c : 0xdfffea;
      container.addChild(new PIXI.Graphics().ellipse(0, 70 * slot.scale, 100 * slot.scale, 122 * slot.scale).stroke({ color, width: 3, alpha: options.queuedActor ? 0.82 : 0.58 }));
    }
    if (options.impact && options.impact.event?.result !== "block") {
      const impactProgress = clamp((options.impact.progress - 0.42) / 0.36, 0, 1);
      const impactAlpha = Math.max(0, 1 - impactProgress);
      if (impactAlpha > 0) {
        container.x += Math.sin(impactProgress * Math.PI * 4) * 6 * impactAlpha;
        container.addChild(new PIXI.Graphics().ellipse(0, 54 * slot.scale, (28 + impactProgress * 70) * slot.scale, (18 + impactProgress * 48) * slot.scale).stroke({ color: options.impact.event?.healing ? 0x72e38b : 0xff684d, width: 5, alpha: impactAlpha }));
      }
    }
    if (options.intentDetails) {
      this.drawCombatIntentStrip(container, fighter, slot, options.intentDetails);
    }
    if (side === "player" && typeof this.drawSkeletalPlayerSprite === "function") {
      this.drawSkeletalPlayerSprite(container, fighter, 0, 92 * slot.scale, slot.scale, {
        alpha: fighter.hp <= 0 ? 0.42 : 1,
        pose: options.acting ? this.playerPoseForCombatEvent(options.acting.event) : "idle",
        animationProgress: options.acting ? options.acting.progress : null,
        actionAnimation: options.acting?.event?.animation || {},
        actionWeaponFamily: options.acting?.event?.animation?.weapon_family || "",
        tilt: options.acting && options.acting.event?.action_type !== "melee" ? Math.sin(options.acting.progress * Math.PI * 2) * 0.035 : 0,
      });
    } else {
      const sprite = new PIXI.Sprite(this.combatTexture(fighter.visual_key, side));
      sprite.anchor.set(0.5, 1);
      sprite.width = 150 * slot.scale;
      sprite.height = 220 * slot.scale;
      sprite.position.set(0, 92 * slot.scale);
      sprite.alpha = fighter.hp <= 0 ? 0.42 : 1;
      if (options.acting && options.acting.event?.action_type !== "melee") {
        sprite.rotation = Math.sin(options.acting.progress * Math.PI * 2) * 0.035;
      }
      container.addChild(sprite);
    }
    const isBoss = side === "enemy" && Boolean(fighter.is_boss);
    const plateOffsetY = 30 * slot.scale;
    const plate = panel(190, 106, { fill: isBoss ? 0x180b08 : 0x070808, alpha: isBoss ? 0.86 : 0.74, stroke: isBoss ? 0xffc35a : 0xd5b062, strokeAlpha: isBoss ? 0.82 : 0.42, radius: 2 });
    plate.position.set(-95, 92 * slot.scale + plateOffsetY);
    const name = text(fighter.name, {
      fontSize: isBoss ? 17 : 13,
      fill: isBoss ? 0xffd36f : 0xf3e7cb,
      fontWeight: "900",
      stroke: isBoss ? { color: 0x3a0804, width: 4 } : undefined,
    });
    name.anchor.set(0.5, 0);
    name.position.set(0, (isBoss ? 94 : 98) * slot.scale + plateOffsetY);
    if (isBoss && name.width > 176) {
      name.scale.set(176 / name.width);
    }
    const bossBadge = isBoss ? panel(54, 18, { fill: 0x5b1510, alpha: 0.94, stroke: 0xffc35a, strokeAlpha: 0.92, radius: 2 }) : null;
    const bossLabel = isBoss ? text("BOSS", { fontSize: 10, fill: 0xfff1c7, fontWeight: "900" }) : null;
    if (bossBadge && bossLabel) {
      bossBadge.position.set(-27, 120 * slot.scale + plateOffsetY);
      bossLabel.anchor.set(0.5);
      bossLabel.position.set(0, 129 * slot.scale + plateOffsetY);
    }
    const blockValues = this.combatantBlockValues(fighter);
    const blockValue = this.totalBlockValue(blockValues);
    const showBlockBadge = blockValue > 0 && (side !== "enemy" || this.state.combatReplay);
    const hpText = text(`Lv ${fighter.level || 1} HP ${Math.max(0, fighter.hp)}/${fighter.max_hp}`, { fontSize: 11, fill: showBlockBadge ? 0xd9d1b2 : 0xb9aa85 });
    hpText.anchor.set(0.5, 0);
    hpText.position.set(0, 117 * slot.scale + plateOffsetY);
    const hpRatio = Math.max(0, Math.min(1, fighter.max_hp > 0 ? fighter.hp / fighter.max_hp : 0));
    const hpTrack = new PIXI.Graphics().roundRect(-80, 137 * slot.scale + plateOffsetY, 160, 8, 4).fill(0x3a0c0a).stroke({ color: 0x080808, width: 1 });
    const hpFill = new PIXI.Graphics().roundRect(-79, 138 * slot.scale + plateOffsetY, Math.max(2, 158 * hpRatio), 6, 3).fill(side === "enemy" ? 0xe06b42 : 0x83d463);
    const planLine = text(options.plans?.length ? `${options.plans.length}/5 planned` : fighter.hp > 0 ? "No action" : "Defeated", { fontSize: 11, fill: 0xe7be69 });
    planLine.anchor.set(0.5, 0);
    planLine.position.set(0, 174 * slot.scale + plateOffsetY);
    container.addChild(plate, name);
    if (bossBadge && bossLabel) {
      container.addChild(bossBadge, bossLabel);
    }
    container.addChild(hpText, hpTrack, hpFill, planLine);
    if (showBlockBadge) {
      this.drawCombatantBlockBadge(container, blockValues, 0, 208 * slot.scale + plateOffsetY, 1, { compact: true, muted: !this.state.combatReplay });
    }
    this.drawCombatantStatusRow(container, fighter, -80, 151 * slot.scale + plateOffsetY, slot.scale);
    return container;
  },

  drawCombatantStatusRow(container, fighter, x, y, scale = 1) {
    const statuses = this.mergedCombatStatuses(fighter.statuses || []);
    if (!statuses.length) {
      return;
    }
    const layer = new PIXI.Container();
    layer.zIndex = 50;
    layer.sortableChildren = true;
    const size = 24 * scale;
    const gap = 6 * scale;
    statuses.slice(0, 7).forEach((status, index) => {
      const iconX = x + index * (size + gap);
      const iconY = y;
      const key = `${fighter.id}:${status.type}`;
      const hovered = this.state.hoveredCombatStatus === key;
      const icon = new PIXI.Container();
      icon.position.set(iconX, iconY);
      icon.eventMode = "static";
      icon.cursor = "help";
      icon.hitArea = new PIXI.Rectangle(0, 0, size, size);
      icon.on("pointerover", (event) => {
        event.stopPropagation?.();
        this.state.hoveredCombatStatus = key;
        this.renderUi();
      });
      icon.on("pointerout", (event) => {
        event.stopPropagation?.();
        if (this.state.hoveredCombatStatus === key) {
          this.state.hoveredCombatStatus = null;
          this.renderUi();
        }
      });
      const spec = this.combatStatusSpec(status);
      const frame = new PIXI.Graphics()
        .roundRect(0, 0, size, size, 5 * scale)
        .fill({ color: spec.fill, alpha: hovered ? 0.98 : 0.84 })
        .stroke({ color: hovered ? 0xfff0bf : spec.stroke, width: hovered ? 2.2 * scale : 1.4 * scale, alpha: 0.95 });
      icon.addChild(frame);
      this.drawCombatStatusIcon(icon, status.type, size / 2, size / 2, size * 0.35, spec.icon);
      const amount = this.combatStatusAmount(status);
      if (amount) {
        const badge = new PIXI.Graphics()
          .roundRect(size - 10 * scale, -3 * scale, 14 * scale, 12 * scale, 5 * scale)
          .fill({ color: 0x090604, alpha: 0.94 })
          .stroke({ color: 0xffe1a1, width: 1 * scale });
        const label = text(amount, { fontSize: 8 * scale, fill: 0xfff3d1, fontWeight: "900" });
        label.anchor.set(0.5);
        label.position.set(size - 3 * scale, 3 * scale);
        icon.addChild(badge, label);
      }
      layer.addChild(icon);
      if (hovered) {
        this.drawCombatStatusTooltip(layer, status, iconX + size / 2, iconY + size + 6 * scale, scale);
      }
    });
    container.addChild(layer);
  },

  mergedCombatStatuses(statuses) {
    const byType = new Map();
    (statuses || []).forEach((status) => {
      const type = String(status.type || "");
      if (!type) return;
      const current = byType.get(type);
      if (current) {
        current.rounds = Math.max(Number(current.rounds || 0), Number(status.rounds || 0));
        current.power = Number(current.power || 0) + Number(status.power || 0);
      } else {
        byType.set(type, { ...status, rounds: Number(status.rounds || 0), power: Number(status.power || 0) });
      }
    });
    const order = ["guard", "dodge", "stun", "poison", "burn", "bleed", "vulnerable", "shaken"];
    return [...byType.values()].sort((left, right) => order.indexOf(left.type) - order.indexOf(right.type));
  },

  combatStatusAmount(status) {
    const power = Math.max(0, Math.round(Number(status.power || 0)));
    if (status.type === "dodge" && power > 0) {
      return `${power}%`;
    }
    if (["poison", "guard", "vulnerable", "shaken"].includes(status.type) && power > 0) {
      return String(power);
    }
    return "";
  },

  combatStatusSpec(status) {
    const specs = {
      guard: { fill: 0x1b3f52, stroke: 0x8ed6ff, icon: 0xd9f2ff, name: "Guard" },
      dodge: { fill: 0x1f3d32, stroke: 0x8fffd8, icon: 0xd7fff4, name: "Dodge" },
      shaken: { fill: 0x4c3265, stroke: 0xc7a1ff, icon: 0xf0deff, name: "Shaken" },
      vulnerable: { fill: 0x5a301f, stroke: 0xff9f68, icon: 0xffe0c7, name: "Vulnerable" },
      poison: { fill: 0x23461f, stroke: 0x91e36e, icon: 0xd8ffbf, name: "Poison" },
      burn: { fill: 0x5b1f12, stroke: 0xff8a3d, icon: 0xffe0a1, name: "Burn" },
      bleed: { fill: 0x5b1218, stroke: 0xff6c78, icon: 0xffccd1, name: "Bleed" },
      stun: { fill: 0x5b4a1a, stroke: 0xffdc73, icon: 0xfff0b8, name: "Stun" },
    };
    return specs[status.type] || { fill: 0x333333, stroke: 0xaaaaaa, icon: 0xffffff, name: this.formatIdLabel(status.type) };
  },

  drawCombatStatusIcon(container, type, x, y, size, color) {
    const icon = new PIXI.Graphics();
    if (type === "guard") {
      icon.moveTo(x, y - size).lineTo(x + size * 0.85, y - size * 0.45).quadraticCurveTo(x + size * 0.65, y + size * 0.75, x, y + size).quadraticCurveTo(x - size * 0.65, y + size * 0.75, x - size * 0.85, y - size * 0.45).closePath().fill({ color }).stroke({ color: 0x050505, width: 1.4 });
    } else if (type === "poison") {
      icon.circle(x, y + size * 0.18, size * 0.62).fill({ color }).circle(x - size * 0.25, y - size * 0.45, size * 0.22).fill({ color }).circle(x + size * 0.25, y - size * 0.45, size * 0.22).fill({ color }).stroke({ color: 0x050505, width: 1.2 });
    } else if (type === "burn") {
      icon.moveTo(x, y + size).quadraticCurveTo(x - size, y + size * 0.1, x - size * 0.15, y - size).quadraticCurveTo(x + size * 0.1, y - size * 0.2, x + size * 0.45, y - size * 0.75).quadraticCurveTo(x + size, y + size * 0.2, x, y + size).fill({ color }).stroke({ color: 0x050505, width: 1.2 });
    } else if (type === "bleed") {
      icon.moveTo(x, y - size).quadraticCurveTo(x + size * 0.8, y, x, y + size).quadraticCurveTo(x - size * 0.8, y, x, y - size).fill({ color }).stroke({ color: 0x050505, width: 1.2 });
    } else if (type === "vulnerable") {
      icon.moveTo(x - size, y - size * 0.5).lineTo(x + size, y - size * 0.5).lineTo(x + size * 0.45, y + size * 0.05).lineTo(x + size, y + size * 0.6).lineTo(x - size, y + size * 0.6).lineTo(x - size * 0.45, y + size * 0.05).closePath().fill({ color }).stroke({ color: 0x050505, width: 1.2 });
    } else if (type === "stun") {
      icon.moveTo(x - size * 0.2, y - size).lineTo(x + size * 0.55, y - size * 0.15).lineTo(x + size * 0.05, y - size * 0.05).lineTo(x + size * 0.28, y + size).lineTo(x - size * 0.55, y).lineTo(x - size * 0.05, y - size * 0.1).closePath().fill({ color }).stroke({ color: 0x050505, width: 1.2 });
    } else if (type === "dodge") {
      icon.ellipse(x, y, size * 0.9, size * 0.48).stroke({ color, width: 1.8 }).moveTo(x - size * 0.65, y + size * 0.15).quadraticCurveTo(x, y - size * 0.8, x + size * 0.65, y + size * 0.15).stroke({ color, width: 1.8 }).circle(x + size * 0.45, y - size * 0.35, size * 0.16).fill({ color }).stroke({ color: 0x050505, width: 1 });
    } else {
      icon.circle(x, y, size * 0.75).stroke({ color, width: 2 }).moveTo(x - size * 0.5, y).quadraticCurveTo(x, y - size * 0.6, x + size * 0.5, y).quadraticCurveTo(x, y + size * 0.6, x - size * 0.5, y).stroke({ color, width: 1.4 });
    }
    container.addChild(icon);
  },

  drawCombatStatusTooltip(container, status, x, y, scale = 1) {
    const spec = this.combatStatusSpec(status);
    const width = 250 * scale;
    const height = 58 * scale;
    const left = Math.max(-125 * scale, Math.min(95 * scale - width, x - width / 2));
    const frame = panel(width, height, { fill: 0x0a0908, alpha: 0.98, stroke: spec.stroke, strokeAlpha: 0.9, radius: 4 });
    frame.position.set(left, y);
    const rounds = Math.max(0, Math.round(Number(status.rounds || 0)));
    const titleText = rounds > 0 ? `${spec.name} (${rounds} turns)` : spec.name;
    const title = text(titleText, { fontSize: 11 * scale, fill: 0xfff0c9, fontWeight: "900" });
    title.position.set(left + 8 * scale, y + 7 * scale);
    const body = text(this.combatStatusDescription(status), { fontSize: 10 * scale, fill: 0xd8ccb0, wordWrap: true, wordWrapWidth: width - 16 * scale });
    body.position.set(left + 8 * scale, y + 25 * scale);
    container.addChild(frame, title, body);
  },

  combatStatusDescription(status) {
    const power = Math.max(0, Math.round(Number(status.power || 0)));
    const descriptions = {
      poison: `2 x ${power} stacks = ${power * 2} damage at round end. Stacks decrease by 1 after damage.`,
      burn: `${power} damage at round end.`,
      bleed: `${power} damage at round end.`,
      guard: `Incoming hit damage -${power * 2}.`,
      dodge: `${power}% chance to completely avoid incoming attacks.`,
      vulnerable: `Incoming hit damage +${Math.max(1, power * 2)}.`,
      shaken: `Outgoing hit damage -${power * 2}.`,
      stun: "Loses planned action while active.",
    };
    return descriptions[status.type] || "Temporary combat effect.";
  },

  drawCombatantBlockBadge(container, blockValues, x, y, scale = 1, options = {}) {
    const values = {
      melee: Math.max(0, Math.round(Number(blockValues?.melee || 0))),
      ranged: Math.max(0, Math.round(Number(blockValues?.ranged || 0))),
      mental: Math.max(0, Math.round(Number(blockValues?.mental || 0))),
    };
    const total = this.totalBlockValue(values);
    if (total <= 0) {
      return;
    }
    const badgeScale = scale * (options.popScale || 1);
    const muted = Boolean(options.muted);
    const width = (options.compact ? 150 : 178) * badgeScale;
    const height = (options.compact ? 34 : 46) * badgeScale;
    const left = x - width / 2;
    const top = y - height / 2;
    const shadow = new PIXI.Graphics()
      .roundRect(left + 3 * badgeScale, top + 4 * badgeScale, width, height, 8 * badgeScale)
      .fill({ color: 0x000000, alpha: 0.46 });
    const frame = new PIXI.Graphics()
      .roundRect(left, top, width, height, 8 * badgeScale)
      .fill({ color: muted ? 0x171717 : 0x16100a, alpha: muted ? 0.82 : 0.94 })
      .stroke({ color: muted ? 0x777777 : 0xffd46d, width: 2.2 * badgeScale, alpha: muted ? 0.7 : 0.98 });
    container.addChild(shadow, frame);

    const shieldX = left + 20 * badgeScale;
    const shieldY = y;
    const shieldSize = (options.compact ? 18 : 24) * badgeScale;
    const shield = new PIXI.Graphics()
      .moveTo(shieldX, shieldY - shieldSize * 0.72)
      .lineTo(shieldX + shieldSize * 0.72, shieldY - shieldSize * 0.28)
      .quadraticCurveTo(shieldX + shieldSize * 0.58, shieldY + shieldSize * 0.65, shieldX, shieldY + shieldSize * 0.9)
      .quadraticCurveTo(shieldX - shieldSize * 0.58, shieldY + shieldSize * 0.65, shieldX - shieldSize * 0.72, shieldY - shieldSize * 0.28)
      .closePath()
      .fill({ color: muted ? 0x8a8a8a : 0xe7c56f, alpha: muted ? 0.9 : 1 })
      .stroke({ color: muted ? 0x2c2c2c : 0x241508, width: 2 * badgeScale });
    container.addChild(shield);

    const zones = [
      ["melee", "M", 0xd15c43],
      ["ranged", "R", 0x55b778],
      ["mental", "Mn", 0x9b83e6],
    ];
    const cellW = (width - 48 * badgeScale) / 3;
    zones.forEach(([zone, label, color], index) => {
      const cellX = left + 39 * badgeScale + index * cellW;
      const value = values[zone];
      const cellColor = muted ? 0x555555 : color;
      const cell = new PIXI.Graphics()
        .roundRect(cellX, top + 7 * badgeScale, cellW - 4 * badgeScale, height - 14 * badgeScale, 5 * badgeScale)
        .fill({ color: value > 0 ? cellColor : 0x2d2923, alpha: value > 0 ? (muted ? 0.76 : 0.9) : 0.5 })
        .stroke({ color: value > 0 ? (muted ? 0xa8a8a8 : 0xffedb6) : 0x5b554d, width: 1.2 * badgeScale });
      const labelText = text(label, { fontSize: (options.compact ? 9 : 11) * badgeScale, fill: muted ? 0xd0d0d0 : 0x16100a, fontWeight: "900" });
      labelText.anchor.set(0, 0.5);
      labelText.position.set(cellX + 4 * badgeScale, y);
      const valueText = text(String(value), { fontSize: (options.compact ? 15 : 20) * badgeScale, fill: muted ? 0xe0e0e0 : 0xfff4cf, fontWeight: "900", stroke: { color: 0x090604, width: 3 * badgeScale } });
      valueText.anchor.set(1, 0.5);
      valueText.position.set(cellX + cellW - 8 * badgeScale, y);
      container.addChild(cell, labelText, valueText);
    });
  },

  combatTexture(visualKey, side) {
    const key = visualKey && this.assetTextures.has(visualKey) ? visualKey : side === "player" ? "hero_melee" : "enemy_humanoid";
    return this.assetTextures.get(key) || this.assetTextures.get(side === "player" ? "hero_melee" : "enemy_humanoid");
  },

  combatControlsLayout() {
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    if (this.isPhoneLandscape()) {
      return phoneCombatControlsLayout(this.state.viewportProfile);
    }
    const controlsHeight = width < 760
      ? Math.min(Math.max(360, height * 0.52), Math.max(320, height - 160))
      : Math.min(310, Math.max(276, height * 0.34));
    return { height: controlsHeight, y: height - controlsHeight - 16 };
  },

  drawCombatControls(combat, players, locked, selfReady, readyCount, partySize, options = {}) {
    const width = this.state.viewport.width;
    const { height: controlsHeight, y } = this.combatControlsLayout();
    const resolving = Boolean(options.resolving);
    const deckPanel = panel(Math.min(width - 26, 980), controlsHeight - 48, { fill: resolving ? 0x111111 : 0x020303, alpha: resolving ? 0.22 : 0.58, stroke: resolving ? 0x777777 : 0xc7a052, strokeAlpha: resolving ? 0.12 : 0.18, radius: 5 });
    deckPanel.position.set(width / 2 - deckPanel.width / 2, y + 40);
    this.layers.modal.addChild(deckPanel);
    const phone = this.isPhoneLandscape();
    const defenseY = y + (phone ? 44 : 52);
    const handY = y + (phone ? 104 : 128);
    this.drawDefenseSlots(defenseY, width, locked || selfReady);
    this.drawCombatHand(handY, width, locked || selfReady);
    this.drawCombatLog(y + controlsHeight - 32, width);
    const guidedMode = this.tutorialCombatMode(combat);
    const chat = button("Fight chat...", 124, 30, () => {
      const inputWidth = Math.min(360, Math.max(220, width - 48));
      this.bridge.show({
        x: phone || width < 760 ? 14 : 28,
        y: Math.max(54, y + 6),
        width: inputWidth,
        value: "",
        placeholder: "Message fight",
        onSubmit: (value) => this.sendCombatChat(value),
      });
    }, { fill: 0x171817, stroke: 0x6f5d3d, fillText: 0xd8cfb6, fontSize: 12 });
    chat.position.set(18, y + 7);
    const readyDisabled = locked || selfReady || !this.state.player || this.state.player.hp <= 0;
    const compact = phone || width < 760;
    const ready = button(selfReady ? "Ready" : "End Turn", 118, 44, readyDisabled ? null : () => this.readyForCombatRound(), {
      fill: readyDisabled ? 0x1a1a18 : 0x5b1d18,
      stroke: readyDisabled ? 0x70644e : 0xe6b859,
      fillText: readyDisabled ? 0x7f735a : 0xfff0c9,
      fontSize: 14,
    });
    ready.position.set(width - 136, compact ? y + 6 : y + 16);
    const readyLabel = text(`${readyCount}/${partySize || 1} ready`, { fontSize: 12, fill: 0xe3c477, fontWeight: "900" });
    readyLabel.anchor.set(0.5, 0);
    readyLabel.position.set(width - 77, compact ? y + 48 : y + 62);
    const fleeDisabled = locked || selfReady || !this.state.player || this.state.player.hp <= 0;
    const flee = button("Flee", 74, 30, fleeDisabled ? null : () => this.fleeCombat(), {
      fill: fleeDisabled ? 0x171716 : 0x2b2119,
      stroke: fleeDisabled ? 0x5a5040 : 0xc49a56,
      fillText: fleeDisabled ? 0x766b58 : 0xf5dfaa,
      fontSize: 12,
    });
    flee.position.set(compact ? width - 218 : width - 134, compact ? y + 13 : y + 84);
    if (guidedMode) {
      const instruction = text(guidedMode === "defense"
        ? (currentLanguage() === "pl" ? "↓ PRZECIĄGNIJ KARTĘ OBRONY DO POLA MELEE, POTEM ZAKOŃCZ TURĘ" : "↓ DRAG A DEFENSE CARD TO MELEE, THEN END THE TURN")
        : (currentLanguage() === "pl" ? "↓ ZAGRAJ KARTĘ ATAKU NA PRZECIWNIKA, POTEM ZAKOŃCZ TURĘ" : "↓ PLAY AN ATTACK CARD ON THE ENEMY, THEN END THE TURN"), {
        fontSize: 13, fill: 0xffe36d, fontWeight: "900", stroke: { color: 0x241600, width: 5 },
      });
      instruction.anchor.set(0.5, 0);
      instruction.position.set(width / 2, y + 10);
      this.layers.modal.addChild(ready, readyLabel, instruction);
    } else {
      this.layers.modal.addChild(chat, ready, readyLabel, flee);
    }
  },

  drawCombatChatPopups(arena, combat, players, width, height) {
    const now = performance.now();
    const popups = (this.state.combatChatPopups || []).filter((popup) => popup.combat_id === combat.id && popup.expires_at > now);
    if (!popups.length) {
      return;
    }
    popups.forEach((popup) => {
      const playerIndex = players.findIndex((fighter) => fighter.id === popup.player_id);
      if (playerIndex < 0) {
        return;
      }
      const slot = this.combatFormationPosition("player", playerIndex, Math.max(1, players.length), width, height);
      const remaining = popup.expires_at - now;
      const alpha = Math.min(0.86, remaining < 1000 ? 0.86 * (remaining / 1000) : 0.86);
      const bubbleWidth = Math.min(190, Math.max(136, width * 0.22));
      const bubble = new PIXI.Container();
      bubble.position.set(slot.x - bubbleWidth / 2, slot.y - 160 * slot.scale);
      const label = text(String(popup.text || "").slice(0, 160), {
        fontSize: 12,
        fill: 0xf4ead2,
        alpha: Math.min(0.9, alpha + 0.05),
        fontWeight: "800",
        wordWrap: true,
        wordWrapWidth: bubbleWidth - 22,
        align: "center",
      });
      label.position.set(11, 9);
      const bubbleHeight = Math.max(34, label.height + 18);
      const frame = panel(bubbleWidth, bubbleHeight, { fill: 0x060707, alpha: 0.55 * alpha, stroke: 0xf0cf8b, strokeAlpha: 0.32 * alpha, radius: 6 });
      const tail = new PIXI.Graphics()
        .moveTo(bubbleWidth / 2 - 8, bubbleHeight - 1)
        .lineTo(bubbleWidth / 2, bubbleHeight + 9)
        .lineTo(bubbleWidth / 2 + 8, bubbleHeight - 1)
        .closePath()
        .fill({ color: 0x060707, alpha: 0.48 * alpha });
      bubble.addChild(frame, tail, label);
      arena.addChild(bubble);
    });
  },

  drawCombatStatusRow(combat, y, width) {
    const selectedCard = this.selectedCombatCard();
    const label = selectedCard ? `${selectedCard.name}: choose target or defense` : "Select a card";
    const selection = panel(Math.min(300, width * 0.28), 36, { fill: 0x0c0d0d, alpha: 0.76, stroke: 0x746448, strokeAlpha: 0.62, radius: 2 });
    selection.position.set(18, y);
    const selectionText = text(label, { fontSize: 13, fill: 0xd8ccb0, fontWeight: "900", wordWrap: true, wordWrapWidth: Math.min(280, width * 0.26) });
    selectionText.position.set(28, y + 10);
    const initiativeWidth = Math.min(420, width * 0.32);
    const initiative = panel(initiativeWidth, 36, { fill: 0x0c0d0d, alpha: 0.76, stroke: 0x746448, strokeAlpha: 0.62, radius: 2 });
    initiative.position.set(width / 2 - initiativeWidth / 2, y);
    const queue = (combat.initiative_queue || []).slice(0, 4).map((entry) => entry.actor_name || entry.action_name).join(" -> ") || "Initiative pending";
    const queueText = text(queue, { fontSize: 12, fill: 0xd8ccb0, wordWrap: true, wordWrapWidth: initiativeWidth - 20 });
    queueText.position.set(initiative.x + 10, y + 10);
    const preview = panel(Math.min(300, width * 0.24), 36, { fill: 0x0c0d0d, alpha: 0.76, stroke: 0x746448, strokeAlpha: 0.62, radius: 2 });
    preview.position.set(width - preview.width - 154, y);
    const counts = this.localBlockCounts();
    const previewText = text(`Block: M ${counts.melee || 0} | R ${counts.ranged || 0} | Ment ${counts.mental || 0}`, { fontSize: 12, fill: 0xd8ccb0, fontWeight: "900" });
    previewText.position.set(preview.x + 10, y + 10);
    this.layers.modal.addChild(selection, selectionText, initiative, queueText, preview, previewText);
  },

  drawDefenseSlots(y, width, disabled) {
    const labels = [["melee", "Melee"], ["ranged", "Ranged"], ["mental", "Mental"]];
    const counts = this.localBlockCounts();
    const totals = this.localBlockValueTotals();
    labels.forEach(([type, label], index) => {
      const rect = this.combatDefenseSlotRect(type, y, width);
      const slotWidth = rect.width;
      const isDropTarget = this.state.combatDrag?.active && this.state.combatDrag.dropTarget?.type === "defense" && this.state.combatDrag.dropTarget.defenseType === type;
      const guidedMelee = this.tutorialCombatMode(this.state.combat) === "defense" && type === "melee";
      const phone = this.isPhoneLandscape();
      const slotHeight = phone ? 54 : 72;
      const slot = panel(slotWidth, slotHeight, {
        fill: disabled ? 0x171514 : isDropTarget ? 0x3d2b18 : 0x201713,
        stroke: isDropTarget || guidedMelee ? 0xffe36d : 0xc49a56,
        alpha: disabled ? 0.54 : 0.86,
        radius: 5,
      });
      slot.position.set(rect.x, rect.y);
      const selectedCard = this.selectedCombatCard();
      const canTap = !disabled && selectedCard?.card_category === "defense" && this.cardCanBlockZone(selectedCard, type);
      if (canTap) {
        slot.eventMode = "static";
        slot.cursor = "pointer";
        slot.hitArea = new PIXI.Rectangle(0, 0, slotWidth, slotHeight);
        slot.on("pointertap", (event) => {
          event.stopPropagation?.();
          this.planCombatCard(selectedCard, "defense", this.state.player?.id, type);
        });
      }
      if (guidedMelee) {
        this.layers.modal.addChild(new PIXI.Graphics().roundRect(rect.x - 4, rect.y - 4, rect.width + 8, rect.height + 8, 8).stroke({ color: 0xffef8a, width: 4, alpha: 0.94 }));
      }
      const title = text(label, { fontSize: phone ? 11 : 13, fill: 0xeadbb9, fontWeight: "900" });
      title.position.set(slot.x + 42, y + 10);
      const count = text(String(totals[type] || counts[type] || 0), { fontSize: phone ? 18 : 22, fill: 0xf3c66f, fontWeight: "900" });
      count.anchor.set(1, 0);
      count.position.set(slot.x + slotWidth - 16, y + 8);
      const shield = new PIXI.Graphics()
        .moveTo(slot.x + 22, y + 11)
        .lineTo(slot.x + 34, y + 17)
        .quadraticCurveTo(slot.x + 32, y + 35, slot.x + 22, y + 42)
        .quadraticCurveTo(slot.x + 12, y + 35, slot.x + 10, y + 17)
        .closePath()
        .fill({ color: 0xd8b971, alpha: 0.96 })
        .stroke({ color: 0x21160d, width: 1.4 });
      const planned = this.ensureLocalCombatPlan().blocks.filter((block) => block.block_zone === type);
      const cardNames = planned.map((block) => this.combatCardByInstance(block.card_instance_id)?.name || block.card_instance_id).join(", ") || "Empty";
      const stack = text(cardNames, { fontSize: phone ? 9 : 11, fill: 0xa99b78, wordWrap: true, wordWrapWidth: slotWidth - 22 });
      stack.position.set(slot.x + 12, y + (phone ? 35 : 38));
      this.layers.modal.addChild(slot, shield, title, count, stack);
    });
  },

  combatDefenseSlotRect(type, y, width) {
    const labels = ["melee", "ranged", "mental"];
    const compact = this.isPhoneLandscape() || width < 760;
    const gap = compact ? 8 : 12;
    const slotWidth = compact
      ? Math.max(92, Math.min(156, (width - 32 - gap * 2) / 3))
      : Math.min(210, (width - 360) / 3);
    const startX = width / 2 - (slotWidth * 3 + gap * 2) / 2;
    const index = Math.max(0, labels.indexOf(type));
    return { x: startX + index * (slotWidth + gap), y, width: slotWidth, height: this.isPhoneLandscape() ? 54 : 72 };
  },

  drawPlannedCasts(y, width) {
    const plan = this.ensureLocalCombatPlan();
    const x = 18;
    const rowWidth = Math.min(520, width - 300);
    const row = panel(rowWidth, 28, { fill: 0x0c0d0d, alpha: 0.66, stroke: 0x746448, strokeAlpha: 0.54, radius: 2 });
    row.position.set(x, y);
    this.layers.modal.addChild(row);
    if (!plan.casts.length) {
      const empty = text("No planned casts.", { fontSize: 12, fill: 0xa99b78 });
      empty.position.set(x + 10, y + 7);
      this.layers.modal.addChild(empty);
      return;
    }
    let tileX = x + 8;
    plan.casts.forEach((cast, index) => {
      const card = this.combatCardByInstance(cast.card_instance_id);
      const label = `${index + 1}. ${card?.name || cast.card_instance_id} -> ${this.combatantName(cast.target_id)}`;
      const tile = button(label, Math.min(170, rowWidth / 3), 22, () => {
        this.state.retargetCombatCardInstanceId = cast.card_instance_id;
        this.state.selectedCombatCardInstanceId = null;
        this.renderUi();
      }, { fill: this.state.retargetCombatCardInstanceId === cast.card_instance_id ? 0x233649 : 0x0e171f, stroke: 0x70889e, fontSize: 10 });
      tile.position.set(tileX, y + 3);
      const cancel = button("X", 22, 22, () => this.cancelLocalPlan(cast.card_instance_id), { fill: 0x3a1512, stroke: 0x9e4f42, fontSize: 10 });
      cancel.position.set(tileX + tile.widthHint + 3, y + 3);
      this.layers.modal.addChild(tile, cancel);
      tileX += tile.widthHint + 30;
    });
  },

  drawCombatHand(y, width, disabled) {
    const hand = this.currentCombatHand();
    const pile = this.state.combat?.card_piles?.[this.state.player?.id] || { draw_pile: 0, discard_pile: 0 };
    const phone = this.isPhoneLandscape();
    const compact = phone || width < 760;
    const pileWidth = compact ? 46 : 62;
    const pileHeight = compact ? 62 : 84;
    const pileY = compact ? y + 104 : y + 18;
    if (!compact) {
      const drawPile = button("", pileWidth, pileHeight, () => this.openCombatPilePopup("draw"), { fill: 0xb89557, alpha: 0.96, stroke: 0xd5b062 });
      drawPile.position.set(34, pileY);
      const drawText = text(`${pile.draw_pile || 0}\nDraw`, { fontSize: 12, fill: 0x1d1712, fontWeight: "900", align: "center" });
      drawText.anchor.set(0.5);
      drawText.position.set(pileWidth / 2, pileHeight / 2);
      drawPile.addChild(drawText);
      const discardX = width - 224;
      const discardPile = button("", pileWidth, pileHeight, () => this.openCombatPilePopup("discard"), { fill: 0x7b6744, alpha: 0.96, stroke: 0xd5b062 });
      discardPile.position.set(discardX, pileY);
      const discardText = text(`${pile.discard_pile || 0}\nDiscard`, { fontSize: 12, fill: 0x1d1712, fontWeight: "900", align: "center" });
      discardText.anchor.set(0.5);
      discardText.position.set(pileWidth / 2, pileHeight / 2);
      discardPile.addChild(discardText);
      this.layers.modal.addChild(drawPile, discardPile);
    }
    const availableWidth = compact ? width - 28 : width - 240;
    const gap = hand.length > 1 ? (compact ? 6 : Math.min(8, (availableWidth - 104 * hand.length) / (hand.length - 1))) : 0;
    const cardWidth = compact
      ? Math.max(78, Math.min(102, (availableWidth - gap * Math.max(0, hand.length - 1)) / Math.max(1, hand.length)))
      : Math.max(104, Math.min(142, availableWidth / Math.max(1, hand.length) + 16));
    const totalWidth = cardWidth * hand.length + gap * Math.max(0, hand.length - 1);
    const startX = compact ? Math.max(14, (width - totalWidth) / 2) : width / 2 - totalWidth / 2;
    const cardViews = hand.map((card, index) => this.combatHandCard(card, {
      x: startX + index * (cardWidth + gap),
      y,
      width: cardWidth,
      height: phone ? 112 : compact ? 138 : 154,
      disabled,
    }));
    cardViews.sort((a, b) => (a.hovered === b.hovered ? 0 : a.hovered ? 1 : -1)).forEach((entry) => this.layers.modal.addChild(entry.view));
  },

  combatPileCenters() {
    const width = this.state.viewport.width;
    const { y } = this.combatControlsLayout();
    const handY = y + (this.isPhoneLandscape() ? 104 : 128);
    return {
      draw: { x: 65, y: handY + 60 },
      discard: { x: width - 224 + 31, y: handY + 60 },
    };
  },

  drawCombatPileShuffleAnimations() {
    if (!this.state.combatPileShuffleAnimations.length) {
      return;
    }
    const now = performance.now();
    const centers = this.combatPileCenters();
    this.state.combatPileShuffleAnimations.forEach((animation) => {
      const progress = clamp((now - animation.startedAt) / animation.duration, 0, 1);
      const eased = progress < 0.5 ? 2 * progress * progress : 1 - ((-2 * progress + 2) ** 2) / 2;
      const copies = Math.min(5, Math.max(2, animation.cardCount || 1));
      for (let index = 0; index < copies; index += 1) {
        const delay = index * 0.06;
        const local = clamp((progress - delay) / Math.max(0.01, 1 - delay), 0, 1);
        const localEased = local < 0.5 ? 2 * local * local : 1 - ((-2 * local + 2) ** 2) / 2;
        const x = centers.discard.x + (centers.draw.x - centers.discard.x) * localEased;
        const y = centers.discard.y + (centers.draw.y - centers.discard.y) * localEased - Math.sin(localEased * Math.PI) * (68 + index * 7);
        const alpha = Math.min(1, local * 3) * Math.max(0, 1 - Math.max(0, local - 0.84) / 0.16);
        const card = panel(34, 46, { fill: 0xd6bc7d, alpha: 0.94 * alpha, stroke: 0xffd56d, strokeAlpha: 0.86 * alpha, radius: 3 });
        card.position.set(x - 17, y - 23);
        card.rotation = -0.18 + localEased * 0.36 + index * 0.035;
        const mark = text("?", { fontSize: 17, fill: 0x211913, fontWeight: "900" });
        mark.anchor.set(0.5);
        mark.alpha = alpha;
        mark.position.set(x, y);
        this.layers.modal.addChild(card, mark);
      }
      const labelAlpha = Math.sin(Math.min(1, eased) * Math.PI);
      const label = text("Shuffling discard into draw", { fontSize: 13, fill: 0xffdf8a, fontWeight: "900", stroke: { color: 0x050505, width: 4 } });
      label.anchor.set(0.5);
      label.alpha = labelAlpha;
      label.position.set((centers.draw.x + centers.discard.x) / 2, centers.draw.y - 96);
      this.layers.modal.addChild(label);
    });
  },

  combatHandCard(card, layout) {
    const planned = layout.preview ? null : this.localPlanForCard(card.instance_id);
    const selected = this.state.selectedCombatCardInstanceId === card.instance_id;
    const hovered = this.state.hoveredCombatCardInstanceId === card.instance_id && !layout.disabled && !this.state.combatDrag;
    const scale = hovered && !planned ? 1.16 : 1;
    const container = new PIXI.Container();
    container._combatCardInstanceId = card.instance_id;
    container.position.set(layout.x + layout.width / 2, layout.y + layout.height / 2 + (hovered && !planned ? -18 : 0));
    container.scale.set(scale);
    container.eventMode = layout.disabled ? "none" : "static";
    container.cursor = container.eventMode === "static" ? "pointer" : "default";
    container.hitArea = new PIXI.Rectangle(-layout.width / 2, -layout.height / 2, layout.width, layout.height);
    container.on("pointerover", (event) => {
      event.stopPropagation?.();
      if (!layout.disabled && this.state.hoveredCombatCardInstanceId !== card.instance_id) {
        this.state.hoveredCombatCardInstanceId = card.instance_id;
        this.renderUi();
      }
    });
    container.on("pointerout", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredCombatCardInstanceId === card.instance_id) {
        this.state.hoveredCombatCardInstanceId = null;
        this.state.hoveredCombatTargetId = null;
        this.renderUi();
      }
    });
    container.on("pointerleave", (event) => {
      event.stopPropagation?.();
      if (this.state.hoveredCombatCardInstanceId === card.instance_id) {
        this.state.hoveredCombatCardInstanceId = null;
        this.state.hoveredCombatTargetId = null;
        this.renderUi();
      }
    });
    if (!layout.disabled && !planned) {
      container.on("pointerdown", (event) => this.beginCombatCardDrag(event, card, layout.width, layout.height));
      container.on("pointertap", (event) => {
        event.stopPropagation?.();
        if (this.state.suppressCombatCardTapInstanceId === card.instance_id) {
          this.state.suppressCombatCardTapInstanceId = null;
          return;
        }
        this.state.selectedCombatCardInstanceId = card.instance_id;
        this.state.retargetCombatCardInstanceId = null;
        this.renderUi();
      });
    }
    this.drawCombatCardFace(container, card, layout, { selected, hovered, planned });
    if (planned) {
      const left = -layout.width / 2;
      const top = -layout.height / 2;
      const overlay = new PIXI.Graphics()
        .roundRect(left + 5, top + 5, layout.width - 10, layout.height - 10, 7)
        .fill({ color: 0xb8b1a1, alpha: hovered ? 0.38 : 0.46 })
        .stroke({ color: hovered ? 0xdfffea : 0xddd0ad, width: hovered ? 2 : 1, alpha: hovered ? 0.88 : 0.46 });
      const label = planned.assignment === "defense"
        ? `BLOCK ${String(planned.defense_type || "").toUpperCase()}`
        : `CAST -> ${this.combatantName(planned.target_id)}`;
      const labelWidth = layout.width - 18;
      const labelBg = new PIXI.Graphics()
        .roundRect(left + 9, top + layout.height * 0.46, labelWidth, 25, 4)
        .fill({ color: 0x050505, alpha: 0.72 })
        .stroke({ color: hovered ? 0xdfffea : 0xe6c06a, width: 1, alpha: 0.82 });
      const plannedText = text(label, {
        fontSize: 10,
        fill: hovered ? 0xdfffea : 0xfff0c8,
        fontWeight: "900",
        align: "center",
        wordWrap: true,
        wordWrapWidth: labelWidth - 8,
        stroke: { color: 0x050505, width: 3 },
      });
      plannedText.anchor.set(0.5);
      plannedText.position.set(0, top + layout.height * 0.46 + 12.5);
      container.addChild(overlay, labelBg, plannedText);
    }
    return { view: container, hovered };
  },

  drawCombatCardFace(container, card, layout, state = {}) {
    const left = -layout.width / 2;
    const top = -layout.height / 2;
    const inner = Math.max(7, Math.min(10, layout.width * 0.07));
    const titleHeight = 22;
    const blockHeight = 30;
    const compactText = layout.compactText === true;
    const outer = new PIXI.Graphics()
      .roundRect(left + 3, top + 3, layout.width - 6, layout.height - 6, 8)
      .fill({ color: state.hovered ? 0x2a241a : 0x15120f, alpha: 0.96 })
      .stroke({ color: state.hovered ? 0xffd978 : 0x7a6137, width: 1.5, alpha: 0.96 });
    const titleBand = new PIXI.Graphics()
      .roundRect(left + inner, top + inner, layout.width - inner * 2, titleHeight, 5)
      .fill({ color: 0xd8b971, alpha: 0.97 })
      .stroke({ color: 0x2a1d12, width: 1 });
    container.addChild(outer, titleBand);
    const title = text(card.name || card.card_id, {
      fontSize: Math.max(10, Math.min(13, layout.width * 0.09)),
      fill: 0x1d1510,
      fontWeight: "900",
      align: "center",
      wordWrap: true,
      wordWrapWidth: layout.width - inner * 2,
    });
    title.anchor.set(0.5);
    title.position.set(0, top + inner + titleHeight / 2);

    const artWidth = layout.width - inner * 2;
    const blockY = top + layout.height - inner - blockHeight;
    const maxArtHeight = Math.max(42, blockY - (top + inner + titleHeight + 5) - (compactText ? 42 : 40));
    const artHeight = compactText
      ? maxArtHeight
      : Math.max(38, Math.min(42, layout.height * 0.3));
    const artX = left + inner;
    const artY = top + inner + titleHeight + 5;
    const artFrame = new PIXI.Graphics()
      .roundRect(artX, artY, artWidth, artHeight, 4)
      .fill({ color: 0x070706, alpha: 0.96 })
      .stroke({ color: state.hovered ? 0xffdf80 : 0x5c4526, width: 1.5, alpha: 0.94 });
    container.addChild(artFrame);
    this.drawCombatCardArt(container, card, artX, artY, artWidth, artHeight);

    const effectY = artY + artHeight + 5;
    const effectHeight = Math.max(30, blockY - effectY - 5);
    const effectPanel = new PIXI.Graphics()
      .roundRect(left + inner, effectY, layout.width - inner * 2, effectHeight, 4)
      .fill({ color: 0xd9bd79, alpha: 0.96 })
      .stroke({ color: 0x3b2a19, width: 1, alpha: 0.8 });
    const tier = Number(card.tier || card.upgrade_level + 1 || 1);
    const tierBadgeWidth = tier > 1 ? Math.max(32, Math.min(42, layout.width * 0.26)) : 0;
    const preview = this.combatCardDamagePreview(card);
    const baseSummary = card.summary || card.effect || card.description || "No effect.";
    const valueLabel = preview
      ? `DMG ${preview.damage}${preview.adjusted ? " vs" : ""}`
      : Number(card.action_value || 0) > 0
        ? `VAL ${card.action_value}`
        : "";
    const valueText = text(valueLabel || baseSummary, {
      fontSize: compactText
        ? Math.max(11, Math.min(13, layout.width * 0.085))
        : valueLabel ? Math.max(13, Math.min(15, layout.width * 0.105)) : Math.max(9, Math.min(11, layout.width * 0.075)),
      fill: valueLabel ? 0x2b170f : 0x2b2118,
      fontWeight: "900",
      align: "center",
      wordWrap: true,
      wordWrapWidth: Math.max(44, layout.width - inner * 2 - 8 - tierBadgeWidth),
    });
    valueText.anchor.set(tier > 1 ? 0 : 0.5, 0);
    valueText.position.set(tier > 1 ? left + inner + 7 : 0, effectY + (compactText ? 7 : 3));
    const summaryText = valueLabel && !compactText ? text(baseSummary.replace(/^Hit\\s+\\d+\\s*/i, ""), {
      fontSize: Math.max(8, Math.min(9, layout.width * 0.065)),
      fill: 0x2b2118,
      fontWeight: "700",
      align: "center",
      wordWrap: true,
      wordWrapWidth: layout.width - inner * 2 - 8,
    }) : null;
    if (summaryText) {
      summaryText.anchor.set(0.5, 0);
      summaryText.position.set(0, effectY + 20);
      const maxSummaryHeight = Math.max(8, effectHeight - 21);
      if (summaryText.height > maxSummaryHeight) {
        summaryText.scale.set(Math.max(0.72, maxSummaryHeight / summaryText.height));
      }
    }
    container.addChild(title, effectPanel, valueText);
    if (summaryText) {
      container.addChild(summaryText);
    }
    this.drawCardTierBadge?.(container, tier, left + layout.width - inner - tierBadgeWidth - 4, effectY + 5, tierBadgeWidth, 21);
    this.drawCombatCardBlockBar(container, card, left + inner, blockY, artWidth, blockHeight);
  },

  drawCombatCardArt(container, card, x, y, width, height) {
    const texture = this.assetTextures.get(this.cardTextureKey(card)) || this.assetTextures.get("card_default");
    if (!texture) {
      const fallback = text(String(card.type || "card").toUpperCase(), { fontSize: 11, fill: 0xd8c28b, fontWeight: "900" });
      fallback.anchor.set(0.5);
      fallback.position.set(x + width / 2, y + height / 2);
      container.addChild(fallback);
      return;
    }
    const sprite = new PIXI.Sprite(texture);
    const scale = Math.max(width / texture.width, height / texture.height);
    sprite.width = texture.width * scale;
    sprite.height = texture.height * scale;
    sprite.position.set(x + (width - sprite.width) / 2, y + (height - sprite.height) / 2);
    sprite.alpha = 1;
    const tier = Number(card.tier || card.upgrade_level + 1 || 1);
    if (tier > 1) {
      const filter = new PIXI.ColorMatrixFilter();
      const tierStep = Math.min(4, tier - 1);
      filter.saturate(1 + tierStep * 0.09, false);
      filter.brightness(1 + tierStep * 0.025, true);
      filter.hue(tierStep * 3, true);
      sprite.filters = [filter];
    }
    const mask = new PIXI.Graphics().roundRect(x, y, width, height, 5).fill(0xffffff);
    sprite.mask = mask;
    container.addChild(sprite, mask);
  },

  cardTextureKey(card) {
    const key = String(card?.icon || "");
    if (key && this.assetTextures.has(key)) {
      return key;
    }
    const fallback = `card_${card?.card_id || ""}`;
    return this.assetTextures.has(fallback) ? fallback : "card_default";
  },

  drawCombatCardBlockBar(container, card, x, y, width, height) {
    const zones = [
      ["melee", 0x7f332a],
      ["ranged", 0x315f46],
      ["mental", 0x514078],
    ];
    const gap = 4;
    const cellWidth = (width - gap * 2) / 3;
    zones.forEach(([zone, color], index) => {
      const cellX = x + index * (cellWidth + gap);
      const value = this.cardDefenseValue(card, zone);
      const cell = new PIXI.Graphics()
        .roundRect(cellX, y, cellWidth, height, 4)
        .fill({ color: value > 0 ? color : 0x2d2923, alpha: value > 0 ? 0.98 : 0.55 })
        .stroke({ color: value > 0 ? 0xffdf8a : 0x5b554d, width: value > 0 ? 1.6 : 1 });
      container.addChild(cell);
      this.drawCardDefenseIcon(container, zone, cellX + 13, y + height / 2, Math.min(15, height * 0.45), value > 0 ? 0xfff1cf : 0x8a8173);
      const valueText = text(value > 0 ? String(value) : "-", { fontSize: Math.max(14, Math.min(18, height * 0.6)), fill: value > 0 ? 0xfff1cf : 0x9a9182, fontWeight: "900", stroke: { color: 0x130d09, width: 2 } });
      valueText.anchor.set(1, 0.5);
      valueText.position.set(cellX + cellWidth - 8, y + height / 2);
      container.addChild(valueText);
    });
  },

  drawCardDefenseIcon(container, zone, x, y, size, color = 0xffe6c0) {
    const icon = new PIXI.Graphics();
    if (zone === "melee") {
      icon
        .moveTo(x - size * 0.45, y + size * 0.42)
        .lineTo(x + size * 0.38, y - size * 0.42)
        .stroke({ color, width: 2.2, alpha: 0.96 })
        .moveTo(x + size * 0.18, y - size * 0.4)
        .lineTo(x + size * 0.4, y - size * 0.42)
        .lineTo(x + size * 0.38, y - size * 0.18)
        .stroke({ color, width: 2.2, alpha: 0.96 });
    } else if (zone === "ranged") {
      icon
        .moveTo(x - size * 0.45, y + size * 0.2)
        .quadraticCurveTo(x, y - size * 0.62, x + size * 0.45, y + size * 0.2)
        .stroke({ color, width: 2.1, alpha: 0.96 })
        .moveTo(x - size * 0.35, y + size * 0.2)
        .lineTo(x + size * 0.35, y + size * 0.2)
        .stroke({ color, width: 1.5, alpha: 0.8 })
        .moveTo(x - size * 0.18, y + size * 0.44)
        .lineTo(x + size * 0.44, y - size * 0.18)
        .stroke({ color, width: 2.1, alpha: 0.96 });
    } else {
      icon
        .circle(x, y, size * 0.34)
        .stroke({ color, width: 2, alpha: 0.94 })
        .moveTo(x, y - size * 0.56)
        .lineTo(x, y + size * 0.56)
        .moveTo(x - size * 0.56, y)
        .lineTo(x + size * 0.56, y)
        .stroke({ color, width: 1.6, alpha: 0.9 });
    }
    container.addChild(icon);
  },

  drawCombatLog(y, width) {
    const log = (this.state.combat?.log || []).slice(-2).join(" | ") || "Combat log empty.";
    const logText = text(log, { fontSize: 12, fill: 0xa99b78, wordWrap: true, wordWrapWidth: width - 36 });
    logText.position.set(18, y);
    this.layers.modal.addChild(logText);
  },

  openCombatPilePopup(type) {
    this.state.combatPilePopup = { type };
    this.renderUi();
  },

  drawCombatPilePopup() {
    const popup = this.state.combatPilePopup;
    if (!popup || !this.state.combat || !this.state.player) {
      return;
    }
    const pile = this.state.combat.card_piles?.[this.state.player.id] || {};
    const isDraw = popup.type === "draw";
    const cards = isDraw ? pile.draw_cards || [] : pile.discard_cards || [];
    const width = Math.min(540, this.state.viewport.width - 56);
    const height = Math.min(470, this.state.viewport.height - 124);
    const x = this.state.viewport.width / 2 - width / 2;
    const y = this.state.viewport.height / 2 - height / 2;
    const blocker = new PIXI.Graphics().rect(0, 0, this.state.viewport.width, this.state.viewport.height).fill({ color: 0x000000, alpha: 0.42 });
    blocker.eventMode = "static";
    blocker.on("pointertap", (event) => {
      event.stopPropagation?.();
      this.state.combatPilePopup = null;
      this.renderUi();
    });
    const frame = panel(width, height, { fill: 0x10100d, alpha: 0.98, stroke: 0xd5b062, strokeAlpha: 0.78, radius: 4 });
    frame.position.set(x, y);
    frame.eventMode = "static";
    frame.on("pointertap", (event) => event.stopPropagation?.());
    const title = text(`${isDraw ? "Draw Pile" : "Discard Pile"} (${cards.length})`, { fontSize: 19, fill: 0xf2e6c7, fontWeight: "900" });
    title.position.set(x + 18, y + 16);
    const close = button("Close", 82, 30, () => {
      this.state.combatPilePopup = null;
      this.renderUi();
    }, { fill: 0x171817, fontSize: 12 });
    close.position.set(x + width - 100, y + 14);
    this.layers.modal.addChild(blocker, frame, title, close);
    if (!cards.length) {
      const empty = text("No cards in this pile.", { fontSize: 14, fill: 0xb9aa85, fontWeight: "700" });
      empty.anchor.set(0.5);
      empty.position.set(x + width / 2, y + height / 2);
      this.layers.modal.addChild(empty);
      return;
    }
    const cardWidth = 118;
    const cardHeight = 154;
    const gap = 12;
    const columns = Math.max(1, Math.floor((width - 36 + gap) / (cardWidth + gap)));
    const listTop = y + 62;
    const visibleRows = Math.max(1, Math.floor((height - 82 + gap) / (cardHeight + gap)));
    const visibleCards = columns * visibleRows;
    cards.slice(0, visibleCards).forEach((card, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const usedWidth = columns * cardWidth + (columns - 1) * gap;
      const cardX = x + width / 2 - usedWidth / 2 + col * (cardWidth + gap);
      const cardY = listTop + row * (cardHeight + gap);
      const cardView = this.combatHandCard(card, {
        x: cardX,
        y: cardY,
        width: cardWidth,
        height: cardHeight,
        disabled: true,
        preview: true,
      });
      this.layers.modal.addChild(cardView.view);
    });
    if (cards.length > visibleCards) {
      const more = text(`+${cards.length - visibleCards} more`, { fontSize: 12, fill: 0xe7be69, fontWeight: "900" });
      more.anchor.set(1, 1);
      more.position.set(x + width - 22, y + height - 12);
      this.layers.modal.addChild(more);
    }
  },

  drawCombatSummary(combat, width, height) {
    this.showCombatRewardEffects(combat);
    const frameWidth = Math.min(820, width - 48);
    const frameHeight = Math.min(560, height - 80);
    const x = width / 2 - frameWidth / 2;
    const y = height / 2 - frameHeight / 2;
    const frame = panel(frameWidth, frameHeight, { fill: 0x10100d, alpha: 0.96, stroke: combat.status === "won" ? 0xd6b05d : 0x9e4f42 });
    frame.position.set(x, y);
    const title = text(this.combatSummaryTitle(combat), { fontSize: 26, fill: combat.status === "won" ? 0xffdf7a : 0xffb1a1, fontWeight: "900" });
    title.position.set(x + 18, y + 16);
    const subtitle = text(`Fight complete after round ${combat.round}.`, { fontSize: 14, fill: 0xb6a680 });
    subtitle.position.set(x + 18, y + 48);
    this.layers.modal.addChild(frame, title, subtitle);
    const content = this.combatRewardSummaryContent(combat, frameWidth - 36);
    const list = this.scrollPanel(content, frameWidth - 36, frameHeight - 126, "combatSummaryScrollOffset");
    list.position.set(x + 18, y + 82);
    const close = button("Continue", 112, 34, () => {
      this.closeCombatSummary(combat);
    });
    close.position.set(x + frameWidth - 114, y + 18);
    this.layers.modal.addChild(list, close);
  },

  combatSummaryTitle(combat) {
    if (combat.status === "won") return "Victory";
    if (combat.status === "escaped") return "Escaped";
    return "Defeat";
  },

  combatRewardSummaryContent(combat, width) {
    const content = new PIXI.Container();
    let y = 0;
    const rewards = combat.rewards?.length ? combat.rewards : [];
    const defeated = (combat.enemies || []).filter((enemy) => enemy.hp <= 0).length;
    const outcome = text(`${defeated}/${(combat.enemies || []).length} enemies defeated`, { fontSize: 13, fill: 0xc8b698, fontWeight: "900" });
    outcome.position.set(0, y);
    content.addChild(outcome);
    y += 26;
    if (!rewards.length) {
      const empty = panel(width, 82, { fill: 0x17130f, alpha: 0.88, stroke: 0x5d4a32, strokeAlpha: 0.7, radius: 4 });
      empty.position.set(0, y);
      const label = text("No rewards earned.", { fontSize: 15, fill: 0xd8c69c, fontWeight: "900" });
      label.position.set(16, y + 28);
      content.addChild(empty, label);
      y += 96;
    } else {
      rewards.forEach((reward) => {
        const card = this.combatRewardPlayerCard(reward, width);
        card.position.set(0, y);
        content.addChild(card);
        y += Number(card.contentHeight || card.height || 120) + 12;
      });
    }
    const logLines = (combat.log || []).slice(-4);
    if (logLines.length) {
      const logTitle = text("Last events", { fontSize: 13, fill: 0xe0b95b, fontWeight: "900" });
      logTitle.position.set(0, y + 4);
      content.addChild(logTitle);
      y += 28;
      logLines.forEach((line) => {
        const entry = text(line, { fontSize: 12, fill: 0xa89976, wordWrap: true, wordWrapWidth: width - 10 });
        entry.position.set(4, y);
        content.addChild(entry);
        y += entry.height + 8;
      });
    }
    content.contentHeight = Math.max(1, y);
    return content;
  },

  combatRewardPlayerCard(reward, width) {
    const container = new PIXI.Container();
    const rarityColors = { common: 0x8a7a5a, uncommon: 0x6ea86b, rare: 0x608fd3, epic: 0x9c6bd3, quest: 0xe0b95b };
    const loot = reward.loot || [];
    const cards = reward.cards || [];
    const rowHeight = 58;
    const itemRows = Math.max(1, loot.length + cards.length);
    const height = 78 + itemRows * rowHeight;
    const border = reward.survived ? 0x7bd889 : 0xb85a43;
    container.addChild(panel(width, height, { fill: 0x17130f, alpha: 0.91, stroke: border, strokeAlpha: 0.64, radius: 5 }));
    const name = text(reward.player_name || "Player", { fontSize: 17, fill: 0xf2e6c7, fontWeight: "900" });
    name.position.set(16, 12);
    const status = text(reward.survived ? "Survived" : "Defeated", { fontSize: 11, fill: reward.survived ? 0x8dffc0 : 0xffa28d, fontWeight: "900" });
    status.position.set(16, 36);
    const levelText = Number(reward.level_after || 0) > Number(reward.level_before || 0)
      ? ` | Lv ${reward.level_before}->${reward.level_after}`
      : "";
    const gains = text(`${reward.xp || 0} XP | ${reward.gold || 0} gold${levelText}`, { fontSize: 13, fill: 0xffdf7a, fontWeight: "900" });
    gains.anchor.set(1, 0);
    gains.position.set(width - 16, 16);
    container.addChild(name, status, gains);
    let y = 66;
    if (!loot.length && !cards.length) {
      const empty = text("No loot", { fontSize: 13, fill: 0xa89976, fontWeight: "900" });
      empty.position.set(18, y + 14);
      container.addChild(empty);
    }
    loot.forEach((item) => {
      const color = rarityColors[item.rarity] || rarityColors.common;
      container.addChild(new PIXI.Graphics().roundRect(16, y, width - 32, 46, 4).fill({ color: 0x211b14, alpha: 0.82 }).stroke({ color, width: 1.5, alpha: 0.82 }));
      const texture = this.itemIconTexture?.(item);
      if (texture) {
        const icon = new PIXI.Sprite(texture);
        icon.anchor.set(0.5);
        icon.position.set(42, y + 23);
        icon.width = 34;
        icon.height = 34;
        container.addChild(icon);
      }
      const itemName = text(`${item.name || this.formatIdLabel(item.item_id)} x${item.quantity || 1}`, { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 190 });
      itemName.position.set(68, y + 7);
      const meta = text(`${this.formatIdLabel(item.rarity || "common")} ${this.formatIdLabel(item.type || "item")}${item.slot ? ` | ${this.formatIdLabel(item.slot)}` : ""}`, { fontSize: 10, fill: 0xc8b698, wordWrap: true, wordWrapWidth: width - 190 });
      meta.position.set(68, y + 27);
      const value = item.value ? text(`${item.value}g`, { fontSize: 11, fill: 0xe0b95b, fontWeight: "900" }) : null;
      if (value) {
        value.anchor.set(1, 0.5);
        value.position.set(width - 24, y + 23);
        container.addChild(value);
      }
      container.addChild(itemName, meta);
      y += rowHeight;
    });
    cards.forEach((card) => {
      const color = rarityColors[card.rarity] || 0xd49cff;
      container.addChild(new PIXI.Graphics().roundRect(16, y, width - 32, 46, 4).fill({ color: 0x1d1624, alpha: 0.86 }).stroke({ color, width: 1.5, alpha: 0.86 }));
      const icon = text("CARD", { fontSize: 10, fill: 0xf3e6ff, fontWeight: "900" });
      icon.position.set(30, y + 17);
      const cardName = text(card.name || this.formatIdLabel(card.card_id), { fontSize: 13, fill: 0xf2e6c7, fontWeight: "900", wordWrap: true, wordWrapWidth: width - 130 });
      cardName.position.set(82, y + 8);
      const meta = text(`${this.formatIdLabel(card.rarity || "common")} card${card.unlocked ? "" : " duplicate"}`, { fontSize: 10, fill: 0xc8b698 });
      meta.position.set(82, y + 28);
      container.addChild(icon, cardName, meta);
      y += rowHeight;
    });
    container.contentHeight = height;
    return container;
  },

  async closeCombatSummary(combat) {
    const followup = this.state.pendingCombatFollowup || combat?.followup_dialogue || null;
    this.state.pendingCombatFollowup = null;
    this.state.dismissedCombatSummaryId = combat?.id || null;
    this.exitCombatView({ force: true });
    this.showCombatLevelUpEffects(combat);
    if (combat?.status === "won" && followup) {
      const opened = await this.openCombatFollowupDialogue(followup);
      if (!opened && followup.label) {
        this.log(`[Quest] ${followup.label}`);
      }
    }
    this.render();
  },

  showCombatRewardEffects(combat) {
    if (!combat?.id || this.state.shownCombatRewardId === combat.id) {
      return;
    }
    this.state.shownCombatRewardId = combat.id;
    (combat.rewards || []).forEach((reward) => {
      const loot = (reward.loot || []).map((item) => `${item.name} x${item.quantity}`).join(", ");
      const cards = (reward.cards || []).map((card) => card.name).join(", ");
      this.log(`[Combat] ${reward.player_name}: +${reward.xp || 0} XP${loot ? ` | Loot: ${loot}` : ""}${cards ? ` | Cards: ${cards}` : ""}.`);
    });
  },

  showCombatLevelUpEffects(combat) {
    (combat?.rewards || []).forEach((reward) => {
      if (Number(reward.level_after || 0) > Number(reward.level_before || 0) && reward.player_id === this.state.player?.id) {
        this.showLevelUpEffect(reward.level_after);
      }
    });
  },

  ensureCombatSelection() {
    const combat = this.state.combat;
    if (!combat) return;
    const livingEnemies = (combat.enemies || []).filter((enemy) => enemy.hp > 0);
    const players = this.combatPlayers().filter((player) => player.hp > 0);
    if (!this.state.selectedCombatTargetId || !livingEnemies.some((enemy) => enemy.id === this.state.selectedCombatTargetId)) {
      this.state.selectedCombatTargetId = livingEnemies[0]?.id || null;
    }
    if (!this.state.selectedCombatAllyTargetId || !players.some((player) => player.id === this.state.selectedCombatAllyTargetId)) {
      this.state.selectedCombatAllyTargetId = players.find((player) => player.id === this.state.player?.id)?.id || players[0]?.id || null;
    }
  },

  combatFormationPosition(side, index, count, width, height) {
    const center = (Math.max(1, count) - 1) / 2;
    const offset = index - center;
    const yGap = count > 4 ? 0.13 : count > 2 ? 0.19 : 0.22;
    const xStagger = index % 2 === 0 ? 0 : 0.035;
    const phone = this.isPhoneLandscape();
    const safe = phone ? this.safeRect() : null;
    const safeTop = phone ? safe.y + 62 : 92;
    const safeBottom = this.combatControlsLayout().y - 24;
    const safeHeight = phone ? Math.max(56, safeBottom - safeTop) : Math.max(260, safeBottom - safeTop);
    const y = safeTop + safeHeight * (0.52 + offset * yGap);
    const scale = phone ? (count > 2 ? 0.5 : 0.58) : count > 3 ? 0.72 : count > 2 ? 0.82 : 0.92;
    if (side === "enemy") {
      return { x: phone ? safe.x + safe.width * (0.76 - xStagger) : width * (0.76 - xStagger), y, scale };
    }
    return { x: phone ? safe.x + safe.width * (0.22 + xStagger) : width * (0.22 + xStagger), y, scale };
  },

  handleCombatantClick(fighter, side) {
    if (!this.state.combat || fighter.hp <= 0) return;
    if (this.state.retargetCombatCardInstanceId && this.retargetLocalCast(fighter.id)) {
      return;
    }
    if (side === "enemy") {
      this.state.selectedCombatTargetId = fighter.id;
    } else {
      this.state.selectedCombatAllyTargetId = fighter.id;
    }
    const selectedCard = this.selectedCombatCard();
    if (selectedCard) {
      if (selectedCard.card_category === "defense" && side === "player") {
        this.log("[Combat] Drag defense cards into a block zone to block.");
        this.state.selectedCombatCardInstanceId = null;
        this.renderUi();
        return;
      }
      if (side === "enemy" && selectedCard.target === "enemy") {
        this.planCombatCard(selectedCard, "action", fighter.id);
        return;
      }
      if (side === "player" && ["ally", "self"].includes(selectedCard.target)) {
        this.planCombatCard(selectedCard, "action", fighter.id);
        return;
      }
    }
    this.renderUi();
  },

  combatPlayers(combat = this.state.combat) {
    if (combat?.players?.length) {
      return combat.players;
    }
    return (this.state.world?.players || []).filter((player) => combat?.participant_ids?.includes(player.id));
  },

  combatEnemyIntent(enemy) {
    return (this.state.combat?.enemy_intents || []).find((candidate) => candidate.actor_id === enemy.id) || null;
  },

  drawCombatIntentStrip(container, fighter, slot, intent) {
    const icons = this.combatEnemyIntentIcons(intent);
    const targetName = this.combatEnemyIntentTargetName(intent);
    if (!icons.length && !targetName) return;
    const iconSize = 36;
    const gap = 6;
    const targetLabel = targetName
      ? text(targetName, { fontSize: 11, fill: 0xffd38d, fontWeight: "900" })
      : null;
    const labelWidth = targetLabel ? Math.min(92, Math.ceil(targetLabel.width)) : 0;
    if (targetLabel && targetLabel.width > labelWidth) {
      targetLabel.scale.x = labelWidth / targetLabel.width;
    }
    const entries = icons.map((iconKey) => {
      const value = this.combatEnemyIntentValue(intent, iconKey);
      const valueLabel = value
        ? text(value, { fontSize: 15, fill: iconKey === "combat_intent_defense" ? 0xaed5ff : 0xffd38d, fontWeight: "900" })
        : null;
      return { iconKey, valueLabel, width: iconSize + (valueLabel ? Math.ceil(valueLabel.width) + 4 : 0) };
    });
    const intentWidth = entries.reduce((sum, entry) => sum + entry.width, 0) + Math.max(0, entries.length - 1) * gap;
    const contentWidth = intentWidth + (targetLabel ? gap + labelWidth : 0);
    const stripWidth = Math.max(42, contentWidth + 18);
    const stripHeight = 44;
    const baseY = -112 * slot.scale;
    const intentBg = panel(stripWidth, stripHeight, { fill: 0x100908, alpha: 0.82, stroke: 0xe2ab52, strokeAlpha: 0.52, radius: 2 });
    intentBg.position.set(-stripWidth / 2, baseY - stripHeight / 2);
    container.addChild(intentBg);
    let cursorX = -contentWidth / 2;
    entries.forEach(({ iconKey, valueLabel }, index) => {
      if (valueLabel) {
        valueLabel.anchor.set(0, 0.5);
        valueLabel.position.set(cursorX, baseY);
        container.addChild(valueLabel);
        cursorX += Math.ceil(valueLabel.width) + 4;
      }
      const iconTexture = this.assetTextures.get(iconKey) || this.assetTextures.get("combat_intent_unknown");
      if (!iconTexture) return;
      const icon = new PIXI.Sprite(iconTexture);
      icon.anchor.set(0.5);
      icon.width = iconSize;
      icon.height = iconSize;
      const phase = this.combatIntentIconPhase(fighter.id, iconKey, index);
      const homeY = baseY + Math.sin(performance.now() / 900 + phase) * 1.15;
      icon.position.set(cursorX + iconSize / 2, homeY);
      icon.onRender = () => {
        icon.y = baseY + Math.sin(performance.now() / 900 + phase) * 1.15;
      };
      container.addChild(icon);
      cursorX += iconSize;
      if (index < entries.length - 1 || targetLabel) cursorX += gap;
    });
    if (targetLabel) {
      targetLabel.anchor.set(0, 0.5);
      targetLabel.position.set(cursorX, baseY);
      container.addChild(targetLabel);
    }
  },

  combatEnemyIntentValue(intent, iconKey) {
    if (iconKey === "combat_intent_attack") {
      const damage = Math.max(0, Number(intent?.estimated_damage || 0));
      const hits = Math.max(1, Number(intent?.hit_count || 1));
      if (!damage) return "";
      const projectedHits = this.combatEnemyIntentProjectedHits(intent) || Array(hits).fill(damage);
      if (hits === 1) return `${projectedHits[0]}`;
      return projectedHits.every((value) => value === projectedHits[0])
        ? `${hits}x${projectedHits[0]}`
        : projectedHits.join("+");
    }
    if (iconKey === "combat_intent_defense") {
      const block = Object.values(intent?.block_values || {}).reduce((sum, value) => sum + Math.max(0, Number(value || 0)), 0);
      return block > 0 ? `${block}` : "";
    }
    return "";
  },

  combatEnemyIntentProjectedHits(targetIntent) {
    const localPlayerId = this.state.player?.id;
    if (!localPlayerId) return null;
    const targetsLocalPlayer = (intent) => {
      const targetIds = intent?.target_ids?.length ? intent.target_ids : [intent?.target_id].filter(Boolean);
      return targetIds.includes(localPlayerId);
    };
    if (!targetsLocalPlayer(targetIntent)) return null;
    const intentByActor = new Map((this.state.combat?.enemy_intents || []).map((intent) => [intent.actor_id, intent]));
    const ordered = [];
    (this.state.combat?.initiative_queue || []).forEach((entry) => {
      const intent = intentByActor.get(entry.actor_id);
      if (intent && !ordered.includes(intent)) ordered.push(intent);
    });
    intentByActor.forEach((intent) => {
      if (!ordered.includes(intent)) ordered.push(intent);
    });
    const pools = { melee: 0, ranged: 0, mental: 0, ...this.localBlockValueTotals() };
    for (const intent of ordered) {
      if (!targetsLocalPlayer(intent) || Number(intent.estimated_damage || 0) <= 0) continue;
      const zone = intent.action_type === "physical" ? "melee" : intent.action_type;
      const hitDamage = Math.max(0, Number(intent.estimated_damage || 0));
      const projected = [];
      for (let index = 0; index < Math.max(1, Number(intent.hit_count || 1)); index += 1) {
        let remaining = hitDamage;
        const matching = Math.min(remaining, Math.max(0, Number(pools[zone] || 0)));
        pools[zone] = Math.max(0, Number(pools[zone] || 0) - matching);
        remaining -= matching;
        for (const fallbackZone of ["melee", "ranged", "mental"]) {
          if (remaining <= 0 || fallbackZone === zone) continue;
          const available = Math.max(0, Number(pools[fallbackZone] || 0));
          const effective = Math.round(available * (2 / 3));
          const usedEffective = Math.min(remaining, effective);
          if (usedEffective <= 0) continue;
          const rawSpent = Math.min(available, Math.round(usedEffective * (3 / 2)));
          pools[fallbackZone] = available - rawSpent;
          remaining -= usedEffective;
        }
        projected.push(Math.max(0, remaining));
      }
      if (intent === targetIntent) return projected;
    }
    return null;
  },

  combatEnemyIntentIcons(intent) {
    const icons = [];
    const addIcon = (iconKey) => {
      if (iconKey && !icons.includes(iconKey)) icons.push(iconKey);
    };
    const blockTotal = Object.values(intent?.block_values || {}).reduce((sum, value) => sum + Math.max(0, Number(value || 0)), 0);
    const isAttack = Number(intent?.estimated_damage || 0) > 0;
    if (isAttack) {
      addIcon("combat_intent_attack");
    }
    if (!isAttack && ["poison", "bleed", "vulnerable", "buff", "debuff", "phase", "focus"].includes(intent?.intent_kind)) {
      addIcon(intent.intent_icon || `combat_intent_${intent.intent_kind}`);
    }
    if (Number(intent?.defense_points || 0) > 0 || blockTotal > 0) {
      addIcon("combat_intent_defense");
    }
    if (!icons.length) {
      addIcon(intent?.intent_icon || "combat_intent_unknown");
    }
    return icons;
  },

  combatEnemyIntentTargetName(intent) {
    if (!intent?.target_id || !intent.target_name) return "";
    const hasTargetedPressure = Number(intent.offense_points || 0) > 0
      || ["poison", "bleed", "vulnerable", "debuff"].includes(intent.intent_kind);
    if (!hasTargetedPressure) return "";
    const targetIds = Array.isArray(intent.target_ids) && intent.target_ids.length ? intent.target_ids : [intent.target_id];
    const combatPlayerIds = new Set(this.combatPlayers().map((player) => player.id));
    const playerTargetCount = targetIds.filter((targetId) => combatPlayerIds.has(targetId)).length;
    if (!playerTargetCount) return "";
    if (intent.target_scope === "all_players" && playerTargetCount > 1) return "All";
    if (intent.target_scope === "random_two" && playerTargetCount > 1) return `${playerTargetCount} targets`;
    return intent.target_name;
  },

  combatIntentIconPhase(enemyId, iconKey, index) {
    const seed = `${enemyId || "enemy"}:${iconKey || "intent"}:${index}`;
    let hash = 0;
    for (let charIndex = 0; charIndex < seed.length; charIndex += 1) {
      hash = (hash * 31 + seed.charCodeAt(charIndex)) % 9973;
    }
    return (hash / 9973) * Math.PI * 2;
  },

  currentCombatHand() {
    return this.state.combat?.hands?.[this.state.player?.id] || [];
  },

  selectedCombatCard() {
    return this.combatCardByInstance(this.state.selectedCombatCardInstanceId);
  },

  combatCardByInstance(instanceId) {
    return this.currentCombatHand().find((card) => card.instance_id === instanceId) || null;
  },

  combatCardDamagePreview(card) {
    const baseDamage = Number(card?.damage_preview || card?.action_value || 0);
    if (!baseDamage || card?.target !== "enemy") {
      return null;
    }
    const targetId = this.combatPreviewTargetId(card);
    if (!targetId) {
      return { damage: baseDamage, adjusted: false };
    }
    const target = (this.state.combat?.enemies || []).find((enemy) => enemy.id === targetId);
    if (!target) {
      return { damage: baseDamage, adjusted: false };
    }
    const targetPreview = card?.damage_preview_by_target?.[targetId] ?? card?.action_value_by_target?.[targetId];
    if (targetPreview !== undefined && targetPreview !== null) {
      return { damage: Number(targetPreview), adjusted: Number(targetPreview) !== baseDamage };
    }
    return { damage: this.adjustDamagePreviewForTarget(baseDamage, target), adjusted: true };
  },

  combatPreviewTargetId(card) {
    const drag = this.state.combatDrag;
    if (drag?.cardInstanceId === card?.instance_id && drag.dropTarget?.type === "combatant" && drag.dropTarget.side === "enemy") {
      return drag.dropTarget.id;
    }
    if (this.state.hoveredCombatTargetId && (this.state.selectedCombatCardInstanceId === card?.instance_id || this.state.hoveredCombatCardInstanceId === card?.instance_id)) {
      return this.state.hoveredCombatTargetId;
    }
    if (this.state.selectedCombatTargetId && this.state.selectedCombatCardInstanceId === card?.instance_id) {
      return this.state.selectedCombatTargetId;
    }
    return null;
  },

  adjustDamagePreviewForTarget(baseDamage, target) {
    let damage = Number(baseDamage || 0);
    for (const status of target.statuses || []) {
      if (status.type === "guard") {
        damage = Math.max(0, damage - Number(status.power || 1) * 2);
      }
      if (status.type === "vulnerable") {
        damage += Math.max(1, Number(status.power || 1) * 2);
      }
    }
    return Math.max(0, Math.round(damage));
  },

  ensureLocalCombatPlan() {
    const combat = this.state.combat;
    if (!combat || !this.state.player) {
      this.state.localCombatPlan = null;
      return { combatId: null, round: 0, casts: [], blocks: [] };
    }
    if (!this.state.localCombatPlan || this.state.localCombatPlan.combatId !== combat.id || this.state.localCombatPlan.round !== combat.round) {
      const submitted = combat.submitted_plans?.[this.state.player.id] || { casts: [], blocks: [] };
      this.state.localCombatPlan = {
        combatId: combat.id,
        round: combat.round,
        casts: (submitted.casts || []).map((cast) => ({
          card_instance_id: cast.card_instance_id,
          target_id: cast.target_id,
          sequence: cast.sequence || 0,
        })),
        blocks: (submitted.blocks || []).map((block) => ({
          card_instance_id: block.card_instance_id,
          block_zone: block.block_zone,
        })),
      };
    }
    this.normalizeLocalCombatPlan();
    return this.state.localCombatPlan;
  },

  normalizeLocalCombatPlan() {
    const plan = this.state.localCombatPlan;
    if (!plan) return;
    const handCards = this.currentCombatHand();
    const cardsByInstance = new Map(handCards.map((card) => [card.instance_id, card]));
    const livingEnemyIds = new Set((this.state.combat?.enemies || []).filter((enemy) => enemy.hp > 0).map((enemy) => enemy.id));
    const livingPlayerIds = new Set(this.combatPlayers().filter((player) => player.hp > 0).map((player) => player.id));
    const seen = new Set();
    plan.casts = (plan.casts || [])
      .filter((cast) => {
      const card = cardsByInstance.get(cast.card_instance_id);
      if (!card || seen.has(cast.card_instance_id)) return false;
        if (card.target === "enemy" && !livingEnemyIds.has(cast.target_id)) return false;
        if (card.target === "ally" && !livingPlayerIds.has(cast.target_id)) return false;
        if (card.target === "self" && cast.target_id !== this.state.player?.id) return false;
        seen.add(cast.card_instance_id);
        return true;
      })
      .map((cast, index) => ({ ...cast, sequence: index }));
    plan.blocks = (plan.blocks || [])
      .filter((block) => {
        const card = cardsByInstance.get(block.card_instance_id);
        if (!card || seen.has(block.card_instance_id) || !["melee", "ranged", "mental"].includes(block.block_zone)) return false;
        if (this.cardDefenseValue(card, block.block_zone) <= 0) return false;
        seen.add(block.card_instance_id);
        return true;
      });
  },

  localPlanForCard(cardInstanceId) {
    const plan = this.ensureLocalCombatPlan();
    const cast = plan.casts.find((candidate) => candidate.card_instance_id === cardInstanceId);
    if (cast) return { assignment: "action", ...cast };
    const block = plan.blocks.find((candidate) => candidate.card_instance_id === cardInstanceId);
    if (block) return { assignment: "defense", defense_type: block.block_zone, ...block };
    return null;
  },

  localBlockCounts() {
    const counts = { melee: 0, ranged: 0, mental: 0 };
    this.ensureLocalCombatPlan().blocks.forEach((block) => {
      counts[block.block_zone] = (counts[block.block_zone] || 0) + 1;
    });
    return counts;
  },

  localBlockValueTotals() {
    const totals = { melee: 0, ranged: 0, mental: 0 };
    this.ensureLocalCombatPlan().blocks.forEach((block) => {
      const zone = block.block_zone;
      if (zone in totals) {
        totals[zone] += this.cardDefenseValue(this.combatCardByInstance(block.card_instance_id), zone);
      }
    });
    return totals;
  },

  totalBlockValue(blockValues) {
    return ["melee", "ranged", "mental"]
      .reduce((sum, zone) => sum + Math.max(0, Number(blockValues?.[zone] || 0)), 0);
  },

  combatantBlockValues(fighter) {
    const empty = { melee: 0, ranged: 0, mental: 0 };
    if (!fighter?.id) {
      return empty;
    }
    const replayValues = fighter.replay_block_values || {};
    const hasReplayValues = ["melee", "ranged", "mental"].some((zone) => Number(replayValues[zone] || 0) > 0);
    const replayFallback = Number(fighter.replay_block_value || 0);
    if (fighter.kind === "enemy") {
      const totals = this.state.combatReplay ? {} : this.state.combat?.defense_totals?.[fighter.id] || {};
      return {
        melee: Math.max(0, Math.round(Number(totals?.melee || 0) + Number(replayValues.melee || (!hasReplayValues ? replayFallback : 0) || 0))),
        ranged: Math.max(0, Math.round(Number(totals?.ranged || 0) + Number(replayValues.ranged || 0))),
        mental: Math.max(0, Math.round(Number(totals?.mental || 0) + Number(replayValues.mental || 0))),
      };
    }
    const totals = this.state.combatReplay
      ? {}
      : fighter.id === this.state.player?.id
      ? this.localBlockValueTotals()
      : this.state.combat?.defense_totals?.[fighter.id] || {};
    const guardTotal = (fighter.statuses || [])
      .filter((status) => status.type === "guard")
      .reduce((sum, status) => sum + Number(status.power || 1) * 2, 0);
    return {
      melee: Math.max(0, Math.round(Number(totals?.melee || 0) + Number(replayValues.melee || (!hasReplayValues ? replayFallback : 0) || 0) + guardTotal)),
      ranged: Math.max(0, Math.round(Number(totals?.ranged || 0) + Number(replayValues.ranged || 0))),
      mental: Math.max(0, Math.round(Number(totals?.mental || 0) + Number(replayValues.mental || 0))),
    };
  },

  combatantBlockValue(fighter) {
    return Math.max(0, Math.round(this.totalBlockValue(this.combatantBlockValues(fighter))));
  },

  planCombatCard(card, assignment, targetId = null, defenseType = null) {
    if (!this.state.player || !this.state.combat || this.state.combat.status !== "active" || !card) return;
    const guidedMode = this.tutorialCombatMode(this.state.combat);
    if (guidedMode === "defense" && (assignment !== "defense" || (defenseType || "melee") !== "melee")) {
      this.log(currentLanguage() === "pl" ? "[Tutorial] Najpierw zablokuj zapowiedziany cios w polu Melee." : "[Tutorial] Block the telegraphed melee strike first.");
      return;
    }
    if (guidedMode === "action" && assignment !== "action") {
      this.log(currentLanguage() === "pl" ? "[Tutorial] Teraz zagraj kartę ataku na przeciwnika." : "[Tutorial] Now play an attack card on the enemy.");
      return;
    }
    const plan = this.ensureLocalCombatPlan();
    plan.casts = plan.casts.filter((cast) => cast.card_instance_id !== card.instance_id);
    plan.blocks = plan.blocks.filter((block) => block.card_instance_id !== card.instance_id);
    if (assignment === "defense") {
      if (this.cardDefenseValue(card, defenseType || "melee") <= 0) {
        this.log("[Combat] This card cannot block that zone.");
        this.renderUi();
        return;
      }
      plan.blocks.push({ card_instance_id: card.instance_id, block_zone: defenseType || "melee" });
    } else {
      const plannedTargetId = card.target === "enemy"
        ? targetId || this.state.selectedCombatTargetId
        : card.target === "ally"
          ? targetId || this.state.selectedCombatAllyTargetId || this.state.player.id
          : this.state.player.id;
      if (!plannedTargetId) return;
      plan.casts.push({ card_instance_id: card.instance_id, target_id: plannedTargetId, sequence: plan.casts.length });
    }
    this.normalizeLocalCombatPlan();
    this.state.selectedCombatCardInstanceId = null;
    this.state.hoveredCombatCardInstanceId = null;
    this.state.retargetCombatCardInstanceId = null;
    this.renderUi();
  },

  tutorialCombatMode(combat = this.state.combat) {
    if (combat?.quest_context?.quest_id !== "fire_on_black_water") return null;
    return combat.quest_context.stage_id === "face_saboteur" && Number(combat.round || 1) === 1 ? "defense" : "action";
  },

  cancelLocalPlan(cardInstanceId) {
    const plan = this.ensureLocalCombatPlan();
    plan.casts = plan.casts.filter((cast) => cast.card_instance_id !== cardInstanceId);
    plan.blocks = plan.blocks.filter((block) => block.card_instance_id !== cardInstanceId);
    if (this.state.retargetCombatCardInstanceId === cardInstanceId) {
      this.state.retargetCombatCardInstanceId = null;
    }
    this.normalizeLocalCombatPlan();
    this.renderUi();
  },

  retargetLocalCast(targetId) {
    const plan = this.ensureLocalCombatPlan();
    const cast = plan.casts.find((candidate) => candidate.card_instance_id === this.state.retargetCombatCardInstanceId);
    const card = cast ? this.combatCardByInstance(cast.card_instance_id) : null;
    if (!cast || !card) {
      this.state.retargetCombatCardInstanceId = null;
      return false;
    }
    const targetIsEnemy = (this.state.combat?.enemies || []).some((enemy) => enemy.id === targetId && enemy.hp > 0);
    const targetIsAlly = this.combatPlayers().some((player) => player.id === targetId && player.hp > 0);
    if (card.target === "enemy" && !targetIsEnemy) return false;
    if (card.target === "ally" && !targetIsAlly) return false;
    cast.target_id = card.target === "self" ? this.state.player.id : targetId;
    this.state.retargetCombatCardInstanceId = null;
    this.normalizeLocalCombatPlan();
    this.renderUi();
    return true;
  },

  getPlannedActions(actorId) {
    if (actorId === this.state.player?.id) {
      const plan = this.ensureLocalCombatPlan();
      return [
        ...plan.casts.map((cast) => ({ actor_id: actorId, assignment: "action", card_instance_id: cast.card_instance_id, target_id: cast.target_id, role: "attacker" })),
        ...plan.blocks.map((block) => ({ actor_id: actorId, assignment: "defense", card_instance_id: block.card_instance_id, defense_type: block.block_zone, role: "defender" })),
      ];
    }
    return (this.state.combat?.planned_actions || []).filter((plan) => plan.actor_id === actorId).sort((a, b) => a.slot_index - b.slot_index);
  },

  combatantName(combatantId) {
    return this.combatPlayers().find((player) => player.id === combatantId)?.name
      || (this.state.combat?.enemies || []).find((enemy) => enemy.id === combatantId)?.name
      || "target";
  },

  combatCardCost(card) {
    return card.type === "defense" || Object.values(card.defense || {}).some((value) => Number(value) > 0) ? "0" : "1";
  },

  cardDefenseValue(card, defenseType) {
    const defense = card?.block_values || card?.defense || {};
    if (defenseType === "melee") {
      return Number(defense.melee ?? defense.physical ?? 0);
    }
    return Number(defense[defenseType] ?? 0);
  },

  cardCanBlockZone(card, defenseType) {
    return this.cardDefenseValue(card, defenseType) > 0;
  },

  cardDefenseText(card) {
    return `Def M ${this.cardDefenseValue(card, "melee")} R ${this.cardDefenseValue(card, "ranged")} Ment ${this.cardDefenseValue(card, "mental")}`;
  },

  async readyForCombatRound() {
    if (!this.state.player || !this.state.combat || this.state.combat.status !== "active") return;
    try {
      this.normalizeLocalCombatPlan();
      const plan = this.ensureLocalCombatPlan();
      const replayBaseCombat = structuredClone(this.state.combat);
      const resolvedCombat = await this.api.request(`/combats/${this.state.combat.id}/plan`, {
        method: "POST",
        body: JSON.stringify({ casts: plan.casts, blocks: plan.blocks, ready: true }),
      });
      this.state.combat = resolvedCombat;
      this.state.localCombatPlan = null;
      const shuffleAnimation = this.combatShuffleAnimationFrom(replayBaseCombat, resolvedCombat);
      await this.playCombatReplay(resolvedCombat, replayBaseCombat);
      this.startCombatPileShuffleAnimation(shuffleAnimation);
      await this.refreshWorldAndCurrentMap();
      await this.finishResolvedCombatView(resolvedCombat);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  },

  async fleeCombat() {
    if (!this.state.player || !this.state.combat || this.state.combat.status !== "active") return;
    try {
      const replayBaseCombat = structuredClone(this.state.combat);
      const resolvedCombat = await this.api.request(`/combats/${this.state.combat.id}/flee`, {
        method: "POST",
      });
      this.state.combat = resolvedCombat;
      this.state.localCombatPlan = null;
      const shuffleAnimation = this.combatShuffleAnimationFrom(replayBaseCombat, resolvedCombat);
      await this.playCombatReplay(resolvedCombat, replayBaseCombat);
      this.startCombatPileShuffleAnimation(shuffleAnimation);
      await this.refreshWorldAndCurrentMap();
      await this.finishResolvedCombatView(resolvedCombat);
      this.render();
    } catch (error) {
      this.log(error.message);
    }
  },

  async resolveTimedOutCombat(combat, requestKey) {
    const replayBaseCombat = structuredClone(combat);
    const plan = this.state.localCombatPlan;
    const canSubmitLocalPlan = plan
      && plan.combatId === combat.id
      && plan.round === combat.round
      && !(combat.ready_actor_ids || []).includes(this.state.player?.id);
    if (canSubmitLocalPlan) {
      this.normalizeLocalCombatPlan();
    }
    if (canSubmitLocalPlan && ((plan.casts || []).length || (plan.blocks || []).length)) {
      try {
        await this.api.request(`/combats/${combat.id}/plan`, {
          method: "POST",
          body: JSON.stringify({ casts: plan.casts, blocks: plan.blocks, ready: false }),
        });
      } catch (error) {
        this.log(`[Combat] Could not submit timeout plan: ${error.message}`);
      }
    }
    const resolvedCombat = await this.api.request(`/combats/${combat.id}`);
    this.state.combat = resolvedCombat;
    this.state.localCombatPlan = null;
    const shuffleAnimation = this.combatShuffleAnimationFrom(replayBaseCombat, resolvedCombat);
    if ((resolvedCombat.combat_events || []).length) {
      await this.playCombatReplay(resolvedCombat, replayBaseCombat);
    }
    this.startCombatPileShuffleAnimation(shuffleAnimation);
    await this.refreshWorldAndCurrentMap();
    await this.finishResolvedCombatView(resolvedCombat);
    if (this.state.combatTimeoutRequestKey === requestKey) {
      this.state.combatTimeoutRequestKey = null;
    }
    if (resolvedCombat.status === "active" && resolvedCombat.round === replayBaseCombat.round && !(resolvedCombat.combat_events || []).length) {
      this.state.combatTimeoutRetryAt = Date.now() + 1000;
    } else {
      this.state.combatTimeoutRetryAt = 0;
    }
    this.render();
  },

  combatShuffleAnimationFrom(previousCombat, nextCombat) {
    const playerId = this.state.player?.id;
    if (!playerId) {
      return null;
    }
    const previousPile = previousCombat?.card_piles?.[playerId] || {};
    const nextPile = nextCombat?.card_piles?.[playerId] || {};
    const previousCount = Number(previousPile.shuffle_count || 0);
    const nextCount = Number(nextPile.shuffle_count || 0);
    if (nextCount <= previousCount) {
      return null;
    }
    return {
      shuffleCount: nextCount,
      cardCount: Math.max(1, Number(previousPile.discard_pile || 0)),
    };
  },

  startCombatPileShuffleAnimation(animation) {
    if (!animation) {
      return;
    }
    this.state.combatPileShuffleAnimations.push({
      ...animation,
      startedAt: performance.now(),
      duration: 1150,
    });
    this.renderUi();
  },

  playCombatReplay(combat, baseCombat = null) {
    const events = (combat?.combat_events || []).filter((event) => event?.actor_id);
    if (!combat || !events.length) {
      return Promise.resolve();
    }
    const replayIdentity = combat.resolution_id || events[0]?.round || combat.round;
    const replayKey = `${combat.id}:${replayIdentity}:${events.length}`;
    if (this.state.combatReplaySeenKeys?.has(replayKey)) {
      return Promise.resolve();
    }
    if (this.state.combatReplayKey === replayKey && this.state.combatReplayPromise) {
      return this.state.combatReplayPromise;
    }
    const gapMs = 420;
    const safeBaseCombat = this.combatReplayBaseCombat(combat, baseCombat);
    this.state.combatReplay = {
      combatId: combat.id,
      round: events[0]?.round || combat.round,
      events,
      baseCombat: safeBaseCombat,
      gapMs,
      startedAt: performance.now(),
      finished: false,
    };
    this.combatReplayAudioMarks = new Set();
    this.playSound(audioPaths.sfx?.combatRoundGong, 0.56);
    this.state.combatReplayKey = replayKey;
    this.state.activeWindow = "combat";
    this.render();
    const totalDuration = events.reduce((sum, event) => sum + this.combatEventDurationMs(event), 0) + Math.max(0, events.length - 1) * gapMs + 220;
    this.state.combatReplayPromise = new Promise((resolve) => {
      window.setTimeout(() => {
        if (this.state.combatReplayKey === replayKey) {
          this.state.combatReplay = null;
          this.state.combatReplayKey = null;
          this.state.combatReplayPromise = null;
        }
        this.state.combatReplaySeenKeys?.add(replayKey);
        if (this.state.combatReplaySeenKeys?.size > 60) {
          this.state.combatReplaySeenKeys = new Set([...this.state.combatReplaySeenKeys].slice(-30));
        }
        resolve();
      }, totalDuration);
    });
    return this.state.combatReplayPromise;
  },

  combatReplayBaseCombat(finalCombat, baseCombat = null) {
    const events = (finalCombat?.combat_events || []).filter((event) => event?.target_id);
    const candidate = baseCombat && baseCombat.id === finalCombat.id ? structuredClone(baseCombat) : structuredClone(finalCombat);
    if (!events.length) {
      return candidate;
    }
    const needsRebuild = events.some((event) => {
      const baseTarget = this.combatantInCombat(candidate, event.target_id);
      const finalTarget = this.combatantInCombat(finalCombat, event.target_id);
      if (!baseTarget || !finalTarget) return false;
      if (event.damage) return Number(baseTarget.hp || 0) <= Number(finalTarget.hp || 0);
      if (event.healing && event.result !== "block") return Number(baseTarget.hp || 0) >= Number(finalTarget.hp || 0);
      return false;
    });
    if (!needsRebuild) {
      return candidate;
    }
    const rebuilt = structuredClone(finalCombat);
    [...events].reverse().forEach((event) => {
      const target = this.combatantInCombat(rebuilt, event.target_id);
      if (!target) return;
      if (event.damage) {
        target.hp = Math.min(Number(target.max_hp || target.hp || 0), Number(target.hp || 0) + Number(event.damage || 0));
      }
      if (event.healing && event.result !== "block") {
        target.hp = Math.max(0, Number(target.hp || 0) - Number(event.healing || 0));
      }
    });
    rebuilt.status = "active";
    return rebuilt;
  },

  combatantInCombat(combat, combatantId) {
    return (combat?.players || []).find((fighter) => fighter.id === combatantId)
      || (combat?.enemies || []).find((fighter) => fighter.id === combatantId)
      || null;
  },

  beginCombatCardDrag(event, card, width, height) {
    if (!card || this.state.combatReplay || this.state.combat?.status !== "active") {
      return;
    }
    event.stopPropagation?.();
    const point = { x: event.global.x, y: event.global.y };
    this.state.combatDrag = {
      cardInstanceId: card.instance_id,
      start: point,
      current: point,
      width,
      height,
      active: false,
      dropTarget: null,
    };
  },

  updateCombatCardDrag(event) {
    const drag = this.state.combatDrag;
    if (!drag) {
      return false;
    }
    event.stopPropagation?.();
    const point = { x: event.global.x, y: event.global.y };
    const moved = Math.hypot(point.x - drag.start.x, point.y - drag.start.y);
    drag.current = point;
    if (moved > 5) {
      drag.active = true;
      drag.dropTarget = this.combatDropTargetAt(point, this.combatCardByInstance(drag.cardInstanceId));
      this.renderUi();
    }
    return true;
  },

  finishCombatCardDrag(event) {
    const drag = this.state.combatDrag;
    if (!drag) {
      return false;
    }
    event.stopPropagation?.();
    this.state.combatDrag = null;
    this.state.hoveredCombatCardInstanceId = null;
    this.state.hoveredCombatTargetId = null;
    const card = this.combatCardByInstance(drag.cardInstanceId);
    if (!drag.active || !card) {
      return false;
    }
    const releasePoint = event?.global ? { x: event.global.x, y: event.global.y } : drag.current;
    const dropTarget = this.combatDropTargetAt(releasePoint, card) || drag.dropTarget;
    this.state.suppressCombatCardTapInstanceId = drag.cardInstanceId;
    window.setTimeout(() => {
      if (this.state.suppressCombatCardTapInstanceId === drag.cardInstanceId) {
        this.state.suppressCombatCardTapInstanceId = null;
      }
    }, 250);
    if (dropTarget?.type === "defense") {
      this.planCombatCard(card, "defense", this.state.player?.id, dropTarget.defenseType);
      return true;
    }
    if (dropTarget?.type === "combatant") {
      this.planCombatCard(card, "action", dropTarget.id);
      return true;
    }
    this.renderUi();
    return true;
  },

  combatDropTargetAt(point, card) {
    if (!card || this.state.activeWindow !== "combat" || this.state.combatReplay || this.state.combat?.status !== "active") {
      return null;
    }
    const width = this.state.viewport.width;
    const height = this.state.viewport.height;
    const controlsY = this.combatControlsLayout().y;
    const phone = this.isPhoneLandscape();
    const defenseY = controlsY + (phone ? 44 : 52);
    let nearestDefense = null;
    for (const type of ["melee", "ranged", "mental"]) {
      if (!this.cardCanBlockZone(card, type)) {
        continue;
      }
      const rect = this.combatDefenseSlotRect(type, defenseY, width);
      if (point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height) {
        return { type: "defense", defenseType: type };
      }
      const marginX = phone || width < 760 ? 18 : 24;
      const marginY = phone || width < 760 ? 34 : 26;
      const expanded = {
        x: rect.x - marginX,
        y: rect.y - marginY,
        width: rect.width + marginX * 2,
        height: rect.height + marginY * 2,
      };
      if (point.x >= expanded.x && point.x <= expanded.x + expanded.width && point.y >= expanded.y && point.y <= expanded.y + expanded.height) {
        const centerX = rect.x + rect.width / 2;
        const centerY = rect.y + rect.height / 2;
        const distance = Math.hypot(point.x - centerX, point.y - centerY);
        if (!nearestDefense || distance < nearestDefense.distance) {
          nearestDefense = { type, distance };
        }
      }
    }
    if (nearestDefense) {
      return { type: "defense", defenseType: nearestDefense.type };
    }
    const players = this.combatPlayers();
    if (card.target === "ally" || card.target === "self") {
      const playerTarget = this.combatantDropTargetAt(point, players, "player", 0, width, height, card.target);
      if (playerTarget) return playerTarget;
    }
    if (card.target === "enemy") {
      const enemyTarget = this.combatantDropTargetAt(point, this.state.combat.enemies || [], "enemy", 0, width, height, card.target);
      if (enemyTarget) return enemyTarget;
    }
    return null;
  },

  combatantDropTargetAt(point, fighters, side, top, width, arenaHeight, targetRule) {
    for (let index = 0; index < fighters.length; index += 1) {
      const fighter = fighters[index];
      if (fighter.hp <= 0) continue;
      if (targetRule === "self" && fighter.id !== this.state.player?.id) continue;
      const slot = this.combatFormationPosition(side, index, Math.max(1, fighters.length), width, arenaHeight);
      const center = { x: slot.x, y: top + slot.y + 18 * slot.scale };
      const hitX = 100 * slot.scale;
      const hitY = 150 * slot.scale;
      const normalized = ((point.x - center.x) ** 2) / (hitX ** 2) + ((point.y - center.y) ** 2) / (hitY ** 2);
      if (normalized <= 1) {
        return { type: "combatant", side, id: fighter.id };
      }
    }
    return null;
  },

  hoveredCombatTargetAt(point) {
    if (this.state.activeWindow !== "combat" || this.state.combatReplay || this.state.combat?.status !== "active") {
      return null;
    }
    const activeCard = this.selectedCombatCard() || this.combatCardByInstance(this.state.hoveredCombatCardInstanceId);
    if (!activeCard || activeCard.target !== "enemy") {
      return null;
    }
    const target = this.combatantDropTargetAt(point, this.state.combat.enemies || [], "enemy", 0, this.state.viewport.width, this.state.viewport.height, "enemy");
    if (!target) {
      return null;
    }
    return { id: target.id };
  },
};
